/* eslint-disable i18next/no-literal-string */
import { useState, useEffect, useMemo, useRef } from 'react';
import { T, Var, useGT } from 'gt-react';
import { TransactionViewV2 } from './TransactionViewV2';
import { PostedUpcEditor } from './PostedUpcEditor';
import { useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import {
  computeClaimAmount,
  computeClaimAmountBreakdown,
} from '@erp/shared-utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  PackageIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CaretDownIcon,
  XIcon,
  PlusIcon,
  FloppyDiskIcon,
  CodeIcon,
  PencilSimpleIcon,
  TrashIcon,
  CheckIcon,
  DownloadSimpleIcon,
  SpinnerIcon,
  ProhibitIcon,
  ArrowUUpLeftIcon,
} from '@phosphor-icons/react';
import type { Transaction } from '@/api/rpc/hooks/ship-and-debit/useE844sdTransactions';
import { useRexelShipAndDebitTransaction } from '@/api/rexel/ship-and-debit/useRexelShipAndDebitTransaction';
import {
  useUpdateStagedTransaction,
  useAddComment,
  useEditComment,
  useDeleteComment,
  isPostedFrozen,
} from '@/api/rpc/hooks/ship-and-debit/useE844sdTransactions';
import { useCompanyHierarchy } from '@/api/rpc/hooks/ship-and-debit/useVDMs';
import useAuth from '@/api/auth/useAuth';
import { useCan } from '@/hooks/useCan';
import { Action, Subject } from '@erp/authz';
import { useToast } from 'src/context/ToastContext';
import {
  formatAccountingCurrency,
  formatCurrency,
  formatNumber,
} from '@/utils/formatters';
import { format } from 'date-fns';
import { stagedDunsWarning } from '../../spa-duns/spaDunsComparison';
import { SpaDunsWarning } from '../../spa-duns/SpaDunsWarning';
import {
  buildPricingUpdates,
  mergeUntouchedPricingFields,
  type PricingFormData,
  type PricingFormField,
} from './pricingFormUpdates';
import {
  parseDecimalInput,
  parseNullableDecimal,
} from './transactionEditableFields';

interface TransactionComment {
  text: string;
  author: string;
  createdAt: string;
}

function formatTo4Decimals(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  return parseFloat(num.toFixed(4)).toString();
}

// Invoice Data quantities are whole units in practice — show them with
// thousands separators and no decimals (1000 → "1,000"). If a value ever
// carries a real fraction, fall back to its plain (comma-less) form so we
// don't hide the decimals.
function formatInvoiceQuantity(
  value: number | string | null | undefined,
): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  if (Number.isInteger(num)) return num.toLocaleString('en');
  return num.toString();
}

function formatDollar(
  value: number | string | null | undefined,
  decimals = 2,
): string {
  if (value === null || value === undefined || value === '') return '\u2014';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '\u2014';
  return formatAccountingCurrency(num, decimals);
}

function formatDollar4(value: number | string | null | undefined): string {
  return formatDollar(value, 4);
}

function txField(transaction: Transaction | null, field: string): unknown {
  if (!transaction) return null;
  return (transaction as unknown as Record<string, unknown>)[field];
}

function parseYYYYMMDD(value: unknown): Date | null {
  const str = String(value);
  if (!/^\d{8}$/.test(str)) return null;
  // Use local date parts to avoid UTC timezone shift (new Date('YYYY-MM-DD') parses as UTC)
  const date = new Date(
    Number(str.substring(0, 4)),
    Number(str.substring(4, 6)) - 1,
    Number(str.substring(6, 8)),
  );
  return isNaN(date.getTime()) ? null : date;
}

function formatDateField(value: unknown): string {
  if (!value) return '';
  const d = parseYYYYMMDD(value);
  return d ? format(d, 'MMM d, yyyy') : '';
}

function transactionToFormData(
  transaction: Transaction | null,
): PricingFormData {
  if (!transaction) {
    return {
      claimAmount: '',
      buyerCost: '',
      coreCostOverride: '',
      contractNo: '',
      dunsLocation: '',
      suppSellLoc: '',
      upcCode: '',
      buyerProductNo: '',
    };
  }
  return {
    claimAmount: formatTo4Decimals(
      txField(transaction, 'claim_amount') as number | null,
    ),
    buyerCost: formatTo4Decimals(
      txField(transaction, 'unit_price_wholesale') as number | null,
    ),
    coreCostOverride: formatTo4Decimals(
      txField(transaction, 'unit_price_quoted') as number | null,
    ),
    contractNo: String(txField(transaction, 'supplier_contract_no') ?? ''),
    dunsLocation: String(txField(transaction, 'duns_gl_branch') ?? ''),
    suppSellLoc: String(txField(transaction, 'supplier_sell_location') ?? ''),
    upcCode: String(txField(transaction, 'upc_code') ?? ''),
    buyerProductNo: String(txField(transaction, 'buyer_product_no') ?? ''),
  };
}

function MetadataItem({
  label,
  value,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] text-muted-foreground leading-tight">{label}</p>
      <div className="text-sm font-medium truncate">{value}</div>
    </div>
  );
}

function EditableField({
  label,
  editing,
  value,
  displayValue,
  onChange,
  inputMode,
  readOnly,
}: {
  label: string;
  editing: boolean;
  value: string;
  displayValue: string;
  onChange?: (value: string) => void;
  inputMode?: 'decimal' | 'text';
  readOnly?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] text-muted-foreground leading-tight">{label}</p>
      {editing ? (
        readOnly ? (
          <div className="h-6 text-sm mt-0.5 flex items-center text-muted-foreground">
            {displayValue}
          </div>
        ) : (
          <Input
            aria-label={label}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            inputMode={inputMode}
            className="h-6 text-sm mt-0.5"
          />
        )
      ) : (
        <div className="text-sm font-medium truncate">{displayValue}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AS/400 Display Mode View
// ---------------------------------------------------------------------------

// NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
const _REBATE_TYPE_LABELS: Record<string, string> = {
  C: 'Contract',
  P: 'Promos / Projects',
  M: 'Manuals',
};

// NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
const _SHIP_TYPE_LABELS: Record<string, string> = {
  '1': 'Regular',
  '2': 'Special',
};

// NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
const _TRANSFER_TYPE_LABELS: Record<string, string> = {
  SD: 'SD - Ship/Debit',
  PS: 'PS - Price Support',
};

const OUTBOUND_FLAG_STYLES: Record<
  string,
  {
    labelKey: string;
    variant: 'default' | 'secondary' | 'outline' | 'success' | 'warning';
  }
> = {
  V: { labelKey: 'Valid', variant: 'success' },
  P: { labelKey: 'Pending', variant: 'warning' },
  N: { labelKey: 'Non-S&D', variant: 'outline' },
  I: { labelKey: 'Internal', variant: 'outline' },
};

/** snake_case DB column → rd* AS400 code (reverse of E844D_CODE_TO_NAME) */
const SNAKE_TO_RD: Record<string, string> = {
  record_type: 'rdzd',
  record_count: 'rdz01',
  company: 'rdcmp',
  division: 'rddiv',
  file_group: 'rdfgrp',
  trans_type: 'rdtrtp',
  trans_sub_type: 'rdtrsb',
  trading_partner: 'rdtrpr',
  duns_gl_branch: 'rddloc',
  supplier_sell_location: 'rdsuplc',
  selling_location: 'rdseloc',
  shipping_location: 'rdshloc',
  short_ship_location_name: 'rdshlocn',
  short_sell_location_name: 'rdselocn',
  branch_name: 'rdbranm',
  customer_name_city: 'rdcustn',
  customer_name: 'rdcunm',
  customer_addr_1: 'rdcua1',
  customer_addr_2: 'rdcua2',
  customer_city: 'rdcucy',
  customer_state: 'rdcupr',
  customer_postal_code: 'rdcupc',
  customer_country: 'rdcuct',
  rebate_type: 'rdrtype',
  supplier_contract_no: 'rdscnt_no',
  agreement_no: 'rdagr_no',
  sold_to_no: 'rdsold',
  ship_to_no: 'rdship',
  order_no: 'rdord_no',
  backorder_level: 'rdbolvl',
  backorder_level_100: 'rdbolvx',
  assigned_id: 'rdpaid',
  transfer_type_code: 'rdttype',
  line_assigned_id: 'rdlinid',
  vendor_catalogue_qualifier: 'rdvccd',
  vendor_catalog_no: 'rdvcc',
  upc_qualifier: 'rdupcd',
  upc_code: 'rdupc',
  vendor_product_qualifier: 'rdvpcd',
  vendor_product_no: 'rdvpc',
  buyer_product_qualifier: 'rdbpcd',
  buyer_product_no: 'rdbpc',
  uom: 'rduom',
  unit_price_wholesale: 'rdwpric',
  basis_uom_wholesale: 'rdwuom',
  unit_price_quoted: 'rdqpric',
  basis_uom_quoted: 'rdquom',
  quantity: 'rdqty',
  quantity_uom: 'rdqtyu',
  claim_amount: 'rdamt',
  invoice_no: 'rdinv_no',
  order_line_no: 'rdlin_no',
  order_line_ref: 'rdlinref',
  invoice_date: 'rdindt',
  ship_date: 'rdshdt',
  order_date: 'rdordt',
  buying_party_currency: 'rdcurb',
  selling_party_currency: 'rdcurs',
  accept_reject: 'rdacpt',
  reject_reason_code: 'rdreacd',
  extract_control_no: 'rdectl_no',
  batch_control_no: 'rdbctl_no',
  buyers_debit_memo: 'rddm_no',
  added_manually: 'rdmana',
  batch_create_date: 'rdbadt',
  batch_create_time: 'rdbatm',
  email_status: 'rdmsts',
  sent_date: 'rdsndt',
  sent_time: 'rdsntm',
  resent_date: 'rdrsdt',
  resent_time: 'rdrstm',
  last_modified_date: 'rdmddt',
  last_modified_time: 'rdmdtm',
  last_modified_by: 'rdmusr',
  last_modified_program: 'rdmpgm',
  outbound_flag: 'rdobfl',
  item_with_prefix: 'rdpitem',
  claim_number: 'rdclaim',
  ship_type: 'rdshtp',
  supplier_credit_memo: 'rdcrmem',
  supplier_batch_id: 'rdbch_no_s',
  vendor_no: 'rdven_no',
  product_on_fly_flag: 'rdptff',
  edi_supplier_flag: 'rdedis',
  previous_status: 'rdprevs',
  landed_cost: 'rdlcost',
  last_po_number: 'rdlpono',
  last_po_vendor: 'rdlvend1',
  price_class: 'rdpcls',
  price_sub_class: 'rdpscls',
  cost_override_flag: 'rdcovfl',
};

/** Convert snake_case E844D record to rd*-keyed record for AS400 display */
function toRdKeyed(
  snakeData: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [snakeKey, value] of Object.entries(snakeData)) {
    const rdKey = SNAKE_TO_RD[snakeKey];
    if (rdKey) {
      result[rdKey] = value;
    }
  }
  return result;
}

function d(data: Record<string, unknown>, key: string): string {
  const v = data[key];
  if (v == null || v === '') return '';
  return String(v).trim();
}

function dn(data: Record<string, unknown>, key: string): number {
  const v = Number(data[key]);
  // Non-finite rather than just NaN: `Number('Infinity')` is Infinity, which
  // has no currency rendering and which the Decimal money helpers reject.
  return Number.isFinite(v) ? v : 0;
}

function fmtCad(n: number): string {
  if (n === 0) return '$0.00';
  return n.toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function fmtCad4(n: number): string {
  if (n === 0) return '$0.0000';
  return n.toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
}

function fmtYmd(raw: unknown): string {
  if (raw == null || raw === '' || raw === 0) return '-';
  const s = String(raw).replace(/\D/g, '');
  if (s.length !== 8) return String(raw);
  return `${s.slice(0, 4)}/${s.slice(4, 6)}/${s.slice(6, 8)}`;
}

function AS400Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-baseline gap-1.5 min-w-0 ${className ?? ''}`}>
      <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0 w-[130px]">
        {label}
      </span>
      <span className="text-xs font-medium truncate">{children}</span>
    </div>
  );
}

function AS400FieldRow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`flex items-baseline gap-4 py-px pl-3 min-w-0 ${className ?? ''}`}
    >
      {children}
    </div>
  );
}

function AS400Section({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 pt-1.5 pb-px">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="flex-1 border-b border-border/50" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Excel download — mirrors build844DReportCsv.ts column layout
// ---------------------------------------------------------------------------

function e844dStr(row: Record<string, unknown>, field: string): string {
  const v = row[field];
  if (v == null) return '';
  return String(v);
}

function e844dNum(row: Record<string, unknown>, field: string): number {
  const v = Number(row[field]);
  return isNaN(v) ? 0 : v;
}

function e844dFmtDate(raw: unknown): string {
  if (raw == null || raw === '') return '';
  const s = String(raw).replace(/\D/g, '');
  if (s.length !== 8) return String(raw);
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

function e844dPrice(n: number): number {
  return Math.round(n * 10000) / 10000;
}

interface ReportCol {
  header: string;
  getValue: (r: Record<string, unknown>) => string | number;
}

const E844D_REPORT_COLUMNS: ReportCol[] = [
  { header: 'Trading Partner Name', getValue: (r) => e844dStr(r, 'rdtrpr') },
  { header: 'Sell Loc Name', getValue: (r) => e844dStr(r, 'rdselocn') },
  { header: 'Ship Loc Name', getValue: (r) => e844dStr(r, 'rdshlocn') },
  { header: 'Bat. Ctl#', getValue: (r) => e844dStr(r, 'rdbctl_no') },
  { header: 'Invoice No.', getValue: (r) => e844dStr(r, 'rdinv_no') },
  { header: 'Order Line No.', getValue: (r) => e844dStr(r, 'rdlin_no') },
  { header: 'Supp Ctct. No.', getValue: (r) => e844dStr(r, 'rdscnt_no') },
  { header: 'Shipto Cust No.', getValue: (r) => e844dStr(r, 'rdship') },
  { header: 'Soldto Cust No.', getValue: (r) => e844dStr(r, 'rdsold') },
  { header: 'Terr. Cus. Br.', getValue: (r) => e844dStr(r, 'rddloc') },
  { header: 'Cust Nm City', getValue: (r) => e844dStr(r, 'rdcustn') },
  { header: 'Our Item #', getValue: (r) => e844dStr(r, 'rdbpc') },
  { header: 'Reb Typ', getValue: (r) => e844dStr(r, 'rdrtype') },
  { header: 'Shp Typ', getValue: (r) => e844dStr(r, 'rdshtp') },
  { header: 'Ship Qty', getValue: (r) => e844dNum(r, 'rdqty') },
  { header: 'Act. Over. Cost', getValue: (r) => e844dNum(r, 'rdqpric') },
  {
    header: 'Ext. Cost Over.',
    getValue: (r) => e844dPrice(e844dNum(r, 'rdqpric') * e844dNum(r, 'rdqty')),
  },
  { header: 'Buyer Cost', getValue: (r) => e844dNum(r, 'rdwpric') },
  {
    header: 'Ext.Buy Cost Over.',
    getValue: (r) => e844dPrice(e844dNum(r, 'rdwpric') * e844dNum(r, 'rdqty')),
  },
  {
    header: 'Inv. Ext. Cost',
    getValue: (r) => e844dPrice(e844dNum(r, 'rdqpric') * e844dNum(r, 'rdqty')),
  },
  {
    header: 'Var. Over Buyer',
    getValue: (r) =>
      e844dPrice(
        e844dNum(r, 'rdamt') - e844dNum(r, 'rdwpric') * e844dNum(r, 'rdqty'),
      ),
  },
  {
    header: 'Var. Over Inv.Cs.',
    getValue: (r) =>
      e844dPrice(
        e844dNum(r, 'rdamt') - e844dNum(r, 'rdqpric') * e844dNum(r, 'rdqty'),
      ),
  },
  { header: 'Claim Amount', getValue: (r) => e844dNum(r, 'rdamt') },
  { header: 'Claim No.', getValue: (r) => e844dStr(r, 'rdclaim') },
  { header: 'Order No.', getValue: (r) => e844dStr(r, 'rdord_no') },
  { header: "B/O Lvl'100-XX", getValue: (r) => e844dStr(r, 'rdbolvx') },
  { header: 'Inv. Date', getValue: (r) => e844dFmtDate(r['rdindt']) },
  { header: 'Prc Mthd', getValue: (r) => e844dStr(r, 'rdttype') },
  { header: 'Our Agreement #', getValue: (r) => e844dStr(r, 'rdagr_no') },
  { header: 'UPC Qualifier', getValue: (r) => e844dStr(r, 'rdupcd') },
  { header: 'UPC Code', getValue: (r) => e844dStr(r, 'rdupc') },
  { header: 'Sell Loc', getValue: (r) => e844dStr(r, 'rdseloc') },
  { header: 'Ship Loc', getValue: (r) => e844dStr(r, 'rdshloc') },
  { header: 'Vendor No.', getValue: (r) => e844dStr(r, 'rdven_no') },
  { header: 'POTF Flag', getValue: (r) => e844dStr(r, 'rdptff') },
  { header: 'EDI Supp. Flag', getValue: (r) => e844dStr(r, 'rdedis') },
  { header: 'Prev Sts', getValue: (r) => e844dStr(r, 'rdprevs') },
  { header: 'Curr Sts', getValue: (r) => e844dStr(r, 'rdmsts') },
  { header: 'Last Receipt Cost', getValue: (r) => e844dNum(r, 'rdlcost') },
  { header: 'Last PO #', getValue: (r) => e844dStr(r, 'rdlpono') },
  { header: 'Last PO Vendor', getValue: (r) => e844dStr(r, 'rdlvend1') },
  { header: 'Prc Grp', getValue: (r) => e844dStr(r, 'rdpcls') },
  { header: 'Vnd Item #', getValue: (r) => e844dStr(r, 'rdvpc') },
  { header: 'Item Desc English', getValue: () => '' }, // filled by caller
  { header: 'Item Desc French', getValue: () => '' }, // filled by caller
  { header: 'GL Bra', getValue: (r) => e844dStr(r, 'rddloc') },
  { header: 'Bra', getValue: (r) => e844dStr(r, 'rdbranm') },
  { header: 'Duns Loc', getValue: (r) => e844dStr(r, 'rddloc') },
  { header: 'SuppXref Loc BPID', getValue: (r) => e844dStr(r, 'rdsuplc') },
  { header: 'Supp-Cust Ship-To BPID', getValue: () => '' },
  { header: 'Supp-Cust Bill-To BPID', getValue: () => '' },
  { header: 'Stk Uom', getValue: (r) => e844dStr(r, 'rduom') },
  { header: 'Prc Uom', getValue: (r) => e844dStr(r, 'rdwuom') },
];

function downloadE844DExcel(
  data: Record<string, unknown>,
  itemDesc?: ItemDescriptions,
) {
  const headers = E844D_REPORT_COLUMNS.map((c) => c.header);
  const values = E844D_REPORT_COLUMNS.map((c) => {
    if (c.header === 'Item Desc English') return itemDesc?.english ?? '';
    if (c.header === 'Item Desc French') return itemDesc?.french ?? '';
    return c.getValue(data);
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, values]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'E844D');
  const invoiceNo = e844dStr(data, 'rdinv_no') || 'transaction';
  const lineNo = e844dStr(data, 'rdlin_no');
  const fileName = lineNo
    ? `E844D_${invoiceNo}_${lineNo}.xlsx`
    : `E844D_${invoiceNo}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

interface ItemDescriptions {
  english: string;
  french: string;
}

/**
 * rd* keys that are editable on the AS/400 OPE84440D screen (underlined fields).
 * Maps rd* key → 'text' | 'decimal' input mode.
 */
const AS400_EDITABLE_RD_KEYS: Record<string, 'text' | 'decimal'> = {
  rdtrpr: 'text', // Trading Partner
  rdrtype: 'text', // Rebate Type
  rdshtp: 'text', // Ship Type
  rdscnt_no: 'text', // Supplier Contract
  rdsuplc: 'text', // Supplier Location
  rdseloc: 'text', // Selling Location
  rdshloc: 'text', // Shipping Location
  rdindt: 'text', // Invoice Date
  rdsold: 'text', // Sold To
  rdship: 'text', // Ship To
  rdwpric: 'decimal', // Wholesale Price
  rdqpric: 'decimal', // Quoted Price
  rdqty: 'decimal', // Quantity
  rdclaim: 'text', // Claim Number
  rdttype: 'text', // Transfer Type
};

/** Reverse map: rd* → snake_case for API submission */
const RD_TO_SNAKE: Record<string, string> = {};
for (const [snake, rd] of Object.entries(SNAKE_TO_RD)) {
  RD_TO_SNAKE[rd] = snake;
}

interface OrderdadDisplayData {
  supplier: string;
  purchaseOrderNumber: number | null;
  poLineNumber: number | null;
  originalOrderNumber: number | null;
  customUse: string;
}

function AS400DisplayView({
  data,
  itemDescriptions,
  orderdadData,
  isEditing,
  editValues,
  onFieldChange,
}: {
  data: Record<string, unknown>;
  itemDescriptions?: ItemDescriptions;
  orderdadData?: OrderdadDisplayData | null;
  isEditing?: boolean;
  editValues?: Record<string, string>;
  onFieldChange?: (rdKey: string, value: string) => void;
}) {
  const gt = useGT();
  /** Render a value — if editing and field is editable, show an input */
  function ev(rdKey: string, displayContent: React.ReactNode): React.ReactNode {
    if (!isEditing || !AS400_EDITABLE_RD_KEYS[rdKey] || !onFieldChange)
      return displayContent;
    return (
      <Input
        value={editValues?.[rdKey] ?? ''}
        onChange={(e) => onFieldChange(rdKey, e.target.value)}
        inputMode={AS400_EDITABLE_RD_KEYS[rdKey]}
        className="h-5 text-xs font-medium w-full max-w-[160px] px-1 py-0 inline-block"
      />
    );
  }

  const wholesalePrice = isEditing
    ? parseFloat(editValues?.rdwpric ?? '0') || 0
    : dn(data, 'rdwpric');
  const quotePrice = isEditing
    ? parseFloat(editValues?.rdqpric ?? '0') || 0
    : dn(data, 'rdqpric');
  const qty = isEditing
    ? parseFloat(editValues?.rdqty ?? '0') || 0
    : dn(data, 'rdqty');
  const claimAmt = dn(data, 'rdamt');
  const unitClaim = wholesalePrice - quotePrice;
  // #3WAMT0 / #3QAMT0 — the AS/400 rounds each extension to 2dp, so this
  // green-screen replica shows the same figures the real screen does.
  const { extendedWholesale, extendedQuoted } = computeClaimAmountBreakdown({
    quantity: qty,
    wholesalePrice,
    quotedPrice: quotePrice,
  });
  const extWholesale = extendedWholesale.toNumber();
  const extQuote = extendedQuoted.toNumber();
  const lastCost = dn(data, 'rdlcost');

  const rebateType = d(data, 'rdrtype');
  const shipType = d(data, 'rdshtp');
  const transferType = d(data, 'rdttype');
  const outboundFlag = d(data, 'rdobfl');
  const costOverride = d(data, 'rdcovfl');

  const obInfo = OUTBOUND_FLAG_STYLES[outboundFlag];

  return (
    <div className="font-mono text-xs leading-tight">
      {/* Status badges */}
      <div className="flex items-center gap-2 pb-1">
        {obInfo && (
          <Badge variant={obInfo.variant}>
            {outboundFlag === 'V'
              ? gt('Valid', {
                  context: 'transaction outbound flag status badge',
                })
              : outboundFlag === 'P'
                ? gt('Pending', {
                    context: 'transaction outbound flag status badge',
                  })
                : outboundFlag === 'N'
                  ? gt('Non-S&D', {
                      context: 'transaction outbound flag status badge',
                    })
                  : gt('Internal', {
                      context: 'transaction outbound flag status badge',
                    })}{' '}
            ({outboundFlag})
          </Badge>
        )}
        {costOverride === 'Y' && (
          <Badge variant="secondary">
            <T context="transaction cost override badge">Cost Override</T>
          </Badge>
        )}
        {d(data, 'rdmana') === 'Y' && (
          <Badge variant="outline">
            <T context="transaction manually added badge">Manual</T>
          </Badge>
        )}
      </div>

      {/* Trading Partner & Type */}
      <AS400Section
        label={gt('Identity', {
          context: 'AS400 section header for trading partner identity',
        })}
      />
      <AS400FieldRow>
        <AS400Field
          label={gt('Trading Partner', { context: 'AS400 field label' })}
        >
          {ev(
            'rdtrpr',
            <span className="font-semibold">{d(data, 'rdtrpr')}</span>,
          )}
        </AS400Field>
      </AS400FieldRow>
      <AS400FieldRow>
        <AS400Field label={gt('Rebate Type', { context: 'AS400 field label' })}>
          {ev(
            'rdrtype',
            <>
              {rebateType}
              {rebateType === 'C'
                ? ` - ${gt('Contract', { context: 'rebate type label' })}`
                : rebateType === 'P'
                  ? ` - ${gt('Promos / Projects', { context: 'rebate type label' })}`
                  : rebateType === 'M'
                    ? ` - ${gt('Manuals', { context: 'rebate type label' })}`
                    : ''}
            </>,
          )}
        </AS400Field>
        <AS400Field
          label={gt('Ship Type', { context: 'AS400 field label' })}
          className="ml-auto"
        >
          {ev(
            'rdshtp',
            <>
              {shipType}
              {shipType === '1'
                ? ` - ${gt('Regular', { context: 'ship type label' })}`
                : shipType === '2'
                  ? ` - ${gt('Special', { context: 'ship type label' })}`
                  : ''}
            </>,
          )}
        </AS400Field>
      </AS400FieldRow>
      <AS400FieldRow>
        <AS400Field
          label={gt('Supplier Contract', { context: 'AS400 field label' })}
        >
          {ev('rdscnt_no', d(data, 'rdscnt_no') || '-')}
        </AS400Field>
        <AS400Field
          label={gt('Supplier Location', { context: 'AS400 field label' })}
          className="ml-auto"
        >
          {ev('rdsuplc', d(data, 'rdsuplc') || '-')}
        </AS400Field>
      </AS400FieldRow>

      {/* Locations */}
      <AS400Section
        label={gt('Locations', { context: 'AS400 section header' })}
      />
      <AS400FieldRow>
        <AS400Field
          label={gt('Selling Location', { context: 'AS400 field label' })}
        >
          {ev(
            'rdseloc',
            <>
              {d(data, 'rdseloc')}
              {d(data, 'rdselocn') ? ` - ${d(data, 'rdselocn')}` : ''}
            </>,
          )}
        </AS400Field>
      </AS400FieldRow>
      <AS400FieldRow>
        <AS400Field
          label={gt('Shipping Location', { context: 'AS400 field label' })}
        >
          {ev(
            'rdshloc',
            <>
              {d(data, 'rdshloc')}
              {d(data, 'rdshlocn') ? ` - ${d(data, 'rdshlocn')}` : ''}
            </>,
          )}
        </AS400Field>
      </AS400FieldRow>
      <AS400FieldRow>
        <AS400Field label={gt('Branch', { context: 'AS400 field label' })}>
          {d(data, 'rdbranm') || '-'}
        </AS400Field>
        <AS400Field
          label={gt('DUNS/GL', {
            context: 'AS400 field label for DUNS GL branch code',
          })}
          className="ml-auto"
        >
          {d(data, 'rddloc') || '-'}
        </AS400Field>
      </AS400FieldRow>

      {/* Dates */}
      <AS400Section label={gt('Dates', { context: 'AS400 section header' })} />
      <AS400FieldRow>
        <AS400Field
          label={gt('Invoice Date', { context: 'AS400 field label' })}
        >
          {ev('rdindt', fmtYmd(data['rdindt']))}
        </AS400Field>
        <AS400Field label={gt('Order Date', { context: 'AS400 field label' })}>
          {fmtYmd(data['rdordt'])}
        </AS400Field>
        <AS400Field label={gt('Ship Date', { context: 'AS400 field label' })}>
          {fmtYmd(data['rdshdt'])}
        </AS400Field>
      </AS400FieldRow>

      {/* Customer */}
      <AS400Section
        label={gt('Customer', { context: 'AS400 section header' })}
      />
      <AS400FieldRow>
        <AS400Field
          label={gt('Sold To', {
            context: 'AS400 field label for sold-to customer',
          })}
        >
          {ev(
            'rdsold',
            <>
              {d(data, 'rdsold')}
              {d(data, 'rdcustn') ? ` - ${d(data, 'rdcustn')}` : ''}
            </>,
          )}
        </AS400Field>
      </AS400FieldRow>
      <AS400FieldRow>
        <AS400Field
          label={gt('Ship To', {
            context: 'AS400 field label for ship-to customer',
          })}
        >
          {ev(
            'rdship',
            <>
              {d(data, 'rdship')}
              {d(data, 'rdcunm') ? ` - ${d(data, 'rdcunm')}` : ''}
            </>,
          )}
        </AS400Field>
      </AS400FieldRow>

      {/* Order Details */}
      <AS400Section
        label={gt('Order', {
          context: 'AS400 section header for order details',
        })}
      />
      <AS400FieldRow>
        <AS400Field
          label={gt('Order #', {
            context: 'AS400 field label for order number',
          })}
        >
          {d(data, 'rdord_no') || '-'}
        </AS400Field>
        <AS400Field
          label={gt('B/O Level', {
            context: 'AS400 field label for backorder level',
          })}
        >
          {d(data, 'rdbolvx') || '-'}
        </AS400Field>
        <AS400Field
          label={gt('Line #', { context: 'AS400 field label for line number' })}
        >
          {d(data, 'rdlin_no') || '-'}
        </AS400Field>
        <AS400Field
          label={gt('Invoice #', {
            context: 'AS400 field label for invoice number',
          })}
        >
          {d(data, 'rdinv_no') || '-'}
        </AS400Field>
      </AS400FieldRow>
      <AS400FieldRow>
        <AS400Field
          label={gt('Transfer Type', { context: 'AS400 field label' })}
        >
          {ev(
            'rdttype',
            <>
              {transferType}
              {transferType === 'SD'
                ? ` (${gt('SD - Ship/Debit', { context: 'transfer type label' })})`
                : transferType === 'PS'
                  ? ` (${gt('PS - Price Support', { context: 'transfer type label' })})`
                  : ''}
            </>,
          )}
        </AS400Field>
        <AS400Field
          label={gt('Claim #', {
            context: 'AS400 field label for claim number',
          })}
        >
          {ev('rdclaim', d(data, 'rdclaim') || '-')}
        </AS400Field>
      </AS400FieldRow>

      {/* ORDERDAD — fetched live from rexel_orderdad */}
      <AS400Section
        label={gt('Order Detail', {
          context: 'AS400 section header for orderdad purchase order data',
        })}
      />
      <AS400FieldRow>
        <AS400Field
          label={gt('P/O Number', {
            context: 'AS400 field label for purchase order number',
          })}
        >
          {String(orderdadData?.purchaseOrderNumber ?? '') || '-'}
        </AS400Field>
        <AS400Field
          label={gt('P/O Line #', {
            context: 'AS400 field label for purchase order line number',
          })}
        >
          {String(orderdadData?.poLineNumber ?? '') || '-'}
        </AS400Field>
        <AS400Field
          label={gt('Original Order #', {
            context: 'AS400 field label for original order number',
          })}
        >
          {String(orderdadData?.originalOrderNumber ?? '') || '-'}
        </AS400Field>
        <AS400Field
          label={gt('Sell Agr. Type', {
            context: 'AS400 field label for sell agreement type',
          })}
        >
          {orderdadData?.customUse?.[0] || '-'}
        </AS400Field>
        <AS400Field
          label={gt('Cost Agr. Type', {
            context: 'AS400 field label for cost agreement type',
          })}
        >
          {orderdadData?.customUse?.[1] || '-'}
        </AS400Field>
      </AS400FieldRow>
      <AS400FieldRow>
        <AS400Field
          label={gt('Cost Agr. #', {
            context: 'AS400 field label for cost agreement number',
          })}
        >
          {orderdadData?.customUse?.slice(2, 7)?.trim() || '-'}
        </AS400Field>
        <AS400Field
          label={gt('Cost Agr. Seq', {
            context: 'AS400 field label for cost agreement sequence',
          })}
        >
          {orderdadData?.customUse?.slice(7, 10)?.trim() || '-'}
        </AS400Field>
      </AS400FieldRow>

      {/* Product */}
      <AS400Section
        label={gt('Product', { context: 'AS400 section header' })}
      />
      <AS400FieldRow>
        <AS400Field
          label={gt('Our Product #', {
            context: 'AS400 field label for internal product number',
          })}
        >
          <span className="font-semibold">
            {d(data, 'rdpitem') || d(data, 'rdbpc') || '-'}
          </span>
        </AS400Field>
        {itemDescriptions?.english && (
          <AS400Field
            label={gt('Description', {
              context: 'AS400 field label for product description',
            })}
          >
            {itemDescriptions.english}
          </AS400Field>
        )}
      </AS400FieldRow>

      {/* Pricing */}
      <AS400Section
        label={gt('Pricing', { context: 'AS400 section header' })}
      />

      {/* Pricing — single table so all columns align perfectly */}
      <div className="pl-3">
        <table className="w-full text-xs table-fixed">
          <colgroup>
            <col style={{ width: '28%' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '24%' }} />
            <col style={{ width: '24%' }} />
          </colgroup>
          <tbody>
            {/* Quantity/UOM/Ag.#/Seq/Ccst row — mirrors AS/400 display */}
            <tr>
              <td className="px-3 py-1 text-muted-foreground">
                <T context="AS400 pricing row label: quantity, unit of measure, agreement number, sequence, cost">
                  Qty/UOM/Ag.#/Seq/Ccst
                </T>
              </td>
              <td className="px-3 py-1 text-right font-medium">
                {ev('rdqty', qty % 1 === 0 ? String(qty) : qty.toFixed(2))}
                &nbsp;
                {d(data, 'rduom') || '-'}
              </td>
              <td className="px-3 py-1 text-right font-medium">
                {d(data, 'rdagr_no') || '-'}&nbsp;&nbsp;
                {d(data, 'rdlinref') || '0'}
              </td>
              <td className="px-3 py-1 text-right font-medium">.0000</td>
            </tr>
            {/* Spacer */}
            <tr>
              <td colSpan={4} className="py-0.5" />
            </tr>
            {/* Header */}
            <tr className="bg-muted/50 border border-border rounded-md">
              <td className="px-3 py-1 font-medium text-muted-foreground" />
              <td className="px-3 py-1 text-right font-medium text-muted-foreground">
                <T context="pricing table column header: unit price">Unit</T>
              </td>
              <td className="px-3 py-1 text-right font-medium text-muted-foreground">
                <T context="pricing table column header: extended or detail amount">
                  Extended / Detail
                </T>
              </td>
              <td className="px-3 py-1 text-right font-medium text-muted-foreground">
                <T context="pricing table column header: approved amount">
                  Approved
                </T>
              </td>
            </tr>
            <tr className="border-t border-border/50">
              <td className="px-3 py-1 text-muted-foreground">
                <T context="AS400 pricing row label: price wholesale">
                  Prc Whsale
                </T>
              </td>
              <td className="px-3 py-1 text-right font-medium">
                {ev('rdwpric', fmtCad4(wholesalePrice))}
              </td>
              <td className="px-3 py-1 text-right font-medium">
                {fmtCad(extWholesale)}
              </td>
              <td className="px-3 py-1 text-right text-muted-foreground">-</td>
            </tr>
            <tr className="border-t border-border/50">
              <td className="px-3 py-1 text-muted-foreground">
                <T context="AS400 pricing row label: price quoted">
                  Prc Quoted
                </T>
              </td>
              <td className="px-3 py-1 text-right font-medium">
                {ev('rdqpric', fmtCad4(quotePrice))}
              </td>
              <td className="px-3 py-1 text-right font-medium">
                {fmtCad(extQuote)}
              </td>
              <td className="px-3 py-1 text-right text-muted-foreground">-</td>
            </tr>
            <tr className="border-t border-border font-semibold">
              <td className="px-3 py-1 text-muted-foreground">
                <T context="AS400 pricing row label: unit claim amount">
                  Unit Claim
                </T>
              </td>
              <td className="px-3 py-1 text-right">{fmtCad(unitClaim)}</td>
              <td className="px-3 py-1 text-right">{fmtCad(claimAmt)}</td>
              <td className="px-3 py-1 text-right text-muted-foreground">-</td>
            </tr>
            {/* Lst PO row */}
            <tr className="border-t border-border/50">
              <td className="px-3 py-1 text-muted-foreground">
                <T context="AS400 pricing row label: last PO cost, number, vendor">
                  Lst PO Cst-#-Vend
                </T>
              </td>
              <td className="px-3 py-1 text-right font-medium">
                {fmtCad4(lastCost)}
              </td>
              <td className="px-3 py-1 text-right font-medium">
                {d(data, 'rdlpono') || '-'} {d(data, 'rdlvend1')}
              </td>
              <td className="px-3 py-1 text-right text-muted-foreground">-</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Modal
// ---------------------------------------------------------------------------

export interface TransactionNavigationInfo {
  /** Zero-based index of the open transaction in the active scope (selected
   *  list or paginated full list). -1 means the open transaction is outside
   *  the active scope (e.g. opened a non-selected row while selections
   *  exist) — the modal renders this as "—/N". */
  currentIndex: number;
  totalItems: number;
  onNavigate: (direction: 'prev' | 'next') => void;
  /** Move forward, falling back to backward, then signal 'end' when there's
   *  no neighbor. Page-callers wire this through their mutation onSuccess so
   *  failed mutations don't navigate away. */
  navigateAfterAction: () => 'advanced' | 'end';
}

type ViewMode = 'default' | 'v2' | 'as400';

interface TransactionViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  onAddToWorkbench?: (transaction: Transaction) => void;
  onStageInternal?: (transaction: Transaction) => void;
  onDelete?: (transaction: Transaction) => void;
  /** Returns a staged transaction back to the pending pool. */
  onUnstage?: (transaction: Transaction) => void;
  /** True while any modal-driven mutation is in flight. Disables the action
   *  CTAs so a double-click can't enqueue duplicate stage/delete/unstage
   *  calls and skip ahead in the navigation queue. */
  isActionPending?: boolean;
  canEdit?: boolean;
  initialView?: ViewMode;
  onViewChange?: (view: ViewMode) => void;
  navigationInfo?: TransactionNavigationInfo;
}

export function TransactionViewModal({
  isOpen,
  onClose,
  transaction,
  onAddToWorkbench,
  onStageInternal,
  onDelete,
  onUnstage,
  isActionPending = false,
  canEdit = true,
  initialView,
  onViewChange,
  navigationInfo,
}: TransactionViewModalProps) {
  const gt = useGT();
  const [inventoryPage, setInventoryPage] = useState(0);
  const INVENTORY_PAGE_SIZE = 5;

  const { currentUser } = useAuth();
  const [canViewV2] = useCan(Action.Read, Subject.SDTransactionV2);

  const [formData, setFormData] = useState<PricingFormData>(
    transactionToFormData(transaction),
  );
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const [_savedFormData, setSavedFormData] = useState<PricingFormData | null>(
    null,
  );
  const [isDirty, setIsDirty] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // Fields the user actually edited. Only these are ever saved — the form can be
  // seeded from a column-pruned list row, where unconfigured columns arrive
  // blank, and diffing those blanks against the full record would silently clear
  // them (see pricingFormUpdates.ts). A ref, not state: nothing renders from it.
  const touchedFieldsRef = useRef<Set<PricingFormField>>(new Set());
  const isEditingRef = useRef(false);
  const lastTransactionIdRef = useRef<string | undefined>(transaction?.id);
  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);
  const [rawE844sdOpen, setRawE844sdOpen] = useState(false);
  const [rawE844dOpen, setRawE844dOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const [_localComments, setLocalComments] = useState<TransactionComment[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>(
    initialView === 'v2' && !canViewV2 ? 'default' : (initialView ?? 'default'),
  );
  const [as400Editing, setAs400Editing] = useState(false);
  const [as400EditValues, setAs400EditValues] = useState<
    Record<string, string>
  >({});
  const [as400Error, setAs400Error] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const updateStagedMutation = useUpdateStagedTransaction();
  const addCommentMutation = useAddComment();
  const editCommentMutation = useEditComment();
  const deleteCommentMutation = useDeleteComment();
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');

  const { data: fullTransactionData, isLoading: isLoadingDetails } =
    useRexelShipAndDebitTransaction(isOpen ? transaction?.id : undefined);
  const fullTransaction = fullTransactionData?.e844d as
    Record<string, unknown> | undefined;

  // Always prefer the fetched e844d record for display — list endpoints return
  // only pruned columns, so the transaction prop may be missing most fields.
  const effectiveTransaction = useMemo(() => {
    if (!transaction) return null;
    if (fullTransaction) {
      return { ...fullTransaction, id: transaction.id } as Transaction;
    }
    return transaction;
  }, [transaction, fullTransaction]);

  // Warn when the DUNS this claim would go out with is not a branch any SPA
  // active on its invoice date names — the supplier rejects that as code 1.
  // V2 shows the saved DUNS and the view toggle does not end an edit, so the
  // edit form only counts while the default view is on screen.
  const dunsMismatch = stagedDunsWarning({
    status: txField(effectiveTransaction, 'status'),
    saved: {
      duns: txField(effectiveTransaction, 'duns_gl_branch'),
      contractNumber: txField(effectiveTransaction, 'supplier_contract_no'),
    },
    invoiceDate: txField(effectiveTransaction, 'invoice_date'),
    edited:
      isEditing && viewMode !== 'v2'
        ? { duns: formData.dunsLocation, contractNumber: formData.contractNo }
        : null,
    spaBuyerCodes: fullTransactionData?.spaBuyerCodes,
  });

  const rawTxStatus = txField(effectiveTransaction, 'status');
  const txStatus = typeof rawTxStatus === 'string' ? rawTxStatus : null;
  // A posted (GL-posted) transaction is frozen — no user or system edit is
  // allowed, only batch-and-send (and a UPC-only edit). Note this is the FROZEN
  // predicate (posted_at set AND still 'staged'), not the bare posted fact:
  // posted_at survives into processing/sent and into a cancelled row's
  // resubmission_pending, none of which are frozen.
  const isPostedTx =
    !!effectiveTransaction && isPostedFrozen(effectiveTransaction);

  useEffect(() => {
    const currentId = effectiveTransaction?.id;
    const isSameTransaction = currentId === lastTransactionIdRef.current;

    // Don't reset for refetches of the same transaction while editing —
    // React Query refetches create new references that would blow away form state.
    // Still reset when navigating to a different transaction.
    if (isEditingRef.current && isSameTransaction) {
      // ...but DO adopt newly-arrived values for fields the user hasn't touched.
      // Edit can be clicked before the full record loads, leaving the form seeded
      // from the pruned list row; without this the blanks would persist on screen.
      setFormData((prev) =>
        mergeUntouchedPricingFields(
          prev,
          transactionToFormData(effectiveTransaction),
          touchedFieldsRef.current,
        ),
      );
      return;
    }

    lastTransactionIdRef.current = currentId;
    touchedFieldsRef.current = new Set();
    setFormData(transactionToFormData(effectiveTransaction));
    setIsDirty(false);
    setIsEditing(false);
    setInventoryPage(0);
    setViewMode(
      initialView === 'v2' && !canViewV2
        ? 'default'
        : (initialView ?? 'default'),
    );
    setAs400Editing(false);
    setAs400EditValues({});
    setAs400Error(null);
    setSaveError(null);

    setCommentText('');
    setEditingCommentId(null);
    setEditingCommentText('');
    const raw = txField(effectiveTransaction, '_comments');
    setLocalComments(Array.isArray(raw) ? (raw as TransactionComment[]) : []);
  }, [transaction, effectiveTransaction, initialView, canViewV2]);

  function handleDialogOpenChange(open: boolean) {
    if (!open) {
      setRawE844sdOpen(false);
      setRawE844dOpen(false);
      setAs400Editing(false);
      setAs400EditValues({});
      setAs400Error(null);
      setSaveError(null);

      onClose();
    }
  }

  const rawE844sdJson = useMemo(() => {
    const raw = fullTransactionData?.e844sdRaw;
    if (!raw) return null;
    const sorted = Object.fromEntries(
      Object.entries(raw).sort(([a], [b]) => a.localeCompare(b)),
    );
    return JSON.stringify(sorted, null, 2);
  }, [fullTransactionData]);

  const rawE844dJson = useMemo(() => {
    const e844d = fullTransactionData?.e844d;
    if (!e844d) return null;
    const sorted = Object.fromEntries(
      Object.entries(e844d).sort(([a], [b]) => a.localeCompare(b)),
    );
    return JSON.stringify(sorted, null, 2);
  }, [fullTransactionData]);

  // Hook must be called before early return to keep hook order stable
  const { companies: companyHierarchy } = useCompanyHierarchy();
  const companyCode = effectiveTransaction
    ? String(txField(effectiveTransaction, 'company') ?? '')
    : '';
  const divisionCode = effectiveTransaction
    ? String(txField(effectiveTransaction, 'division') ?? '')
    : '';
  const branchCode = effectiveTransaction
    ? String(txField(effectiveTransaction, 'selling_location') ?? '')
    : '';
  const locationLookup = useMemo(() => {
    const comp = companyHierarchy.find((c) => c.code === companyCode);
    const div = comp?.divisions.find((d) => d.code === divisionCode);
    const branch = div?.branches.find((b) => b.code === branchCode);
    return {
      companyName: comp?.name ?? '',
      divisionName: div?.name ?? '',
      branchName: branch?.description ?? '',
    };
  }, [companyHierarchy, companyCode, divisionCode, branchCode]);

  if (!effectiveTransaction) return null;

  const ftField = (field: string): unknown => {
    if (!fullTransaction) return null;
    // Try E844D field first, then enrichment_data for E844SD-specific fields
    const val = fullTransaction[field];
    if (val !== undefined) return val;
    const enrichment = fullTransaction.enrichment_data as Record<
      string,
      unknown
    > | null;
    return enrichment?.[field] ?? null;
  };

  // Agreement dates not available in E844D
  const isAgreementExpired = false;

  const handleInputChange = (field: PricingFormField, value: string) => {
    touchedFieldsRef.current.add(field);
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Live recalculate claim amount (OPE84440 formula) when pricing fields change.
      // Shares the backend's definition so the preview can't disagree with the
      // value the server recalculates on save — the old float arithmetic here
      // rounded half-cents the other way.
      if (field === 'buyerCost' || field === 'coreCostOverride') {
        const quantity = Number(txField(effectiveTransaction, 'quantity')) || 0;
        const wholesalePrice = parseFloat(next.buyerCost) || 0;
        const quotedPrice = parseFloat(next.coreCostOverride) || 0;
        // These are free-text inputs, so a pasted "Infinity" or "1e309"
        // survives `|| 0` (it is not NaN) and the Decimal money helpers throw
        // on it. Leave the last good preview in place rather than throwing out
        // of an event handler; the save path rejects the value separately via
        // parseDecimalInput.
        if ([quantity, wholesalePrice, quotedPrice].every(Number.isFinite)) {
          const claimAmount = computeClaimAmount({
            quantity,
            wholesalePrice,
            quotedPrice,
          });
          next.claimAmount = formatTo4Decimals(claimAmount.toNumber());
        }
        // Locally derived — mark it touched so a late-arriving record can't
        // stomp the preview back to the server's pre-edit claim amount.
        touchedFieldsRef.current.add('claimAmount');
      }
      return next;
    });
    setIsDirty(true);
  };

  const handleSave = () => {
    if (!transaction) return;
    // Posted transactions are frozen — never persist edits (the backend's
    // assertTransactionEditable refuses the same set). Say so instead of
    // returning silently: this guard used to over-match (it tested the bare
    // posted fact, so a cancelled-but-previously-posted row read as frozen) and the
    // only symptom a user could see was a Save button that did nothing at all.
    // A refusal the user can't see is worse than the refusal.
    if (isPostedTx) {
      setSaveError(
        gt(
          'This transaction has been GL-posted and is frozen — only its UPC can be changed. Un-post it to edit other fields.',
          {
            context:
              'error shown when trying to save an edit to a GL-posted (frozen) transaction',
          },
        ),
      );
      return;
    }
    // Only fields the user actually touched are sent, diffed against the FULL
    // record — never the pruned list row the form may have been seeded from.
    // claim_amount is never sent: OPE84440 recalculates it from the pricing
    // fields. UPC is editable for any supplier — the "UPC required" submission
    // rule is assignable per-supplier, not Rockwell-only.
    const { updates, invalidFields } = buildPricingUpdates(
      formData,
      transactionToFormData(effectiveTransaction),
      touchedFieldsRef.current,
    );
    // A price that isn't a complete number must block the whole save. Writing
    // the parseable prefix would persist a plausible wrong price (and recalc the
    // claim from it); writing null would clear it. Both are silent money bugs.
    if (invalidFields.length > 0) {
      setSaveError(
        `${gt('Enter a valid number for:', {
          context: 'error shown when a price field is not a valid number',
        })} ${invalidFields.join(', ')}`,
      );
      return;
    }
    if (Object.keys(updates).length > 0) {
      setSaveError(null);
      updateStagedMutation.mutate(
        {
          transactionId: effectiveTransaction.id,
          updates,
        },
        {
          onSuccess: () => {
            touchedFieldsRef.current = new Set();
            setIsDirty(false);
            setIsEditing(false);
            setSaveError(null);
            showToast({
              message: gt('Transaction updated successfully', {
                context: 'success toast message',
              }),
              title: gt('Success', { context: 'success toast title' }),
              color: 'success',
              icon: 'check',
            });
            queryClient.invalidateQueries({
              queryKey: [
                `rexel/ship-and-debit/844/transactions/${effectiveTransaction.id}`,
              ],
            });
          },
          onError: (error: Error) => {
            setSaveError(
              error.message ||
                gt('Failed to save changes', {
                  context: 'error message when saving transaction fails',
                }),
            );
          },
        },
      );
    } else {
      // Nothing to persist (every touched field was reverted). Still end the
      // session cleanly — see the reset on entering edit mode for why a stale
      // touched set is dangerous.
      touchedFieldsRef.current = new Set();
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    touchedFieldsRef.current = new Set();
    setFormData(transactionToFormData(effectiveTransaction));
    setSavedFormData(null);
    setIsDirty(false);
    setIsEditing(false);
    setSaveError(null);
  };

  const handleAddComment = () => {
    if (!transaction || !commentText.trim()) return;
    const text = commentText.trim();
    setCommentText('');
    addCommentMutation.mutate(
      { transactionId: effectiveTransaction.id, text },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [
              `rexel/ship-and-debit/844/transactions/${effectiveTransaction.id}`,
            ],
          });
        },
        onError: () => {
          setCommentText(text);
        },
      },
    );
  };

  // Action handlers do NOT close the modal — the parent's callback decides
  // whether to advance to the next selected transaction or close the modal.
  // `isActionPending` short-circuits double-fires: even if the disabled-state
  // on the button misses a fast click (e.g. via a keyboard shortcut), the
  // handler itself refuses to enqueue another mutation while one is running.
  const handleAddToWorkbench = () => {
    if (isActionPending) return;
    // Posted transactions are frozen — no re-staging.
    if (isPostedTx) return;
    onAddToWorkbench?.(effectiveTransaction);
  };

  const handleStageInternal = () => {
    if (isActionPending) return;
    // Posted transactions are frozen — no state change but batch-and-send.
    if (isPostedTx) return;
    onStageInternal?.(effectiveTransaction);
  };

  const handleDelete = () => {
    if (isActionPending) return;
    // Posted transactions are frozen — can't be marked no-claim.
    if (isPostedTx) return;
    onDelete?.(effectiveTransaction);
  };

  const handleUnstage = () => {
    if (isActionPending) return;
    // Posted transactions are frozen — never unstage (backend refuses too).
    if (isPostedTx) return;
    onUnstage?.(effectiveTransaction);
  };

  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _itemDescription = (() => {
    const nonListed = null;
    return nonListed
      ? String(nonListed)
      : (fullTransactionData?.invoiceMasterData?.[0]?.invoiceDescription ?? '');
  })();

  const customerName = String(
    txField(effectiveTransaction, 'customer_name') ?? '',
  );
  const customerNo = String(txField(effectiveTransaction, 'sold_to_no') ?? '');
  const customerDisplay = customerName
    ? customerNo
      ? `${customerName} (${customerNo})`
      : customerName
    : customerNo || '';
  const customerLocation = [
    txField(effectiveTransaction, 'customer_city'),
    txField(effectiveTransaction, 'customer_state'),
    txField(effectiveTransaction, 'customer_postal_code'),
  ]
    .filter(Boolean)
    .map(String)
    .join(', ');

  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _tradingPartner = String(
    txField(effectiveTransaction, 'trading_partner') ?? '',
  );
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _tradingPartnerCode = String(
    txField(effectiveTransaction, 'trading_partner_code') ??
      txField(effectiveTransaction, 'trading_partner') ??
      '',
  );

  const agreementNumber = String(ftField('agreement_no') ?? '');
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _agreementType = String(ftField('rebate_type') ?? '');
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _agreementRef = String(ftField('supplier_contract_no') ?? '');

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="!max-w-[90vw] sm:!max-w-[1400px] h-[90vh] flex flex-col"
        // Base UI's spelling of Radix's `onOpenAutoFocus` + `preventDefault`.
        initialFocus={false}
      >
        <DialogHeader className="pb-1">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base flex items-center gap-2">
              <T context="modal title for transaction details with invoice number">
                Transaction Details - INV #{' '}
                <Var>
                  {String(txField(effectiveTransaction, 'invoice_no') ?? '')}
                </Var>
              </T>
              {txStatus && (
                <Badge
                  variant="outline"
                  className="text-[10px] font-normal capitalize"
                >
                  {txStatus.replace(/_/g, ' ')}
                </Badge>
              )}
            </DialogTitle>
            <div className="flex items-center gap-4 mr-6">
              {navigationInfo && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => navigationInfo.onNavigate('prev')}
                    disabled={
                      navigationInfo.currentIndex <= 0 &&
                      navigationInfo.currentIndex !== -1
                    }
                    className="p-1 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <CaretLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-muted-foreground tabular-nums min-w-[4ch] text-center">
                    {navigationInfo.currentIndex < 0
                      ? '—'
                      : navigationInfo.currentIndex + 1}{' '}
                    / {navigationInfo.totalItems}
                  </span>
                  <button
                    type="button"
                    onClick={() => navigationInfo.onNavigate('next')}
                    disabled={
                      navigationInfo.currentIndex !== -1 &&
                      navigationInfo.currentIndex >=
                        navigationInfo.totalItems - 1
                    }
                    className="p-1 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <CaretRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
              {(() => {
                const modes = canViewV2
                  ? (['default', 'v2', 'as400'] as const)
                  : (['default', 'as400'] as const);
                return (
                  <div className="flex items-center rounded-md border text-xs">
                    {modes.map((mode, i) => (
                      <button
                        key={mode}
                        type="button"
                        className={`px-2.5 py-1 transition-colors ${
                          viewMode === mode
                            ? 'bg-foreground text-background font-medium'
                            : 'text-muted-foreground hover:text-foreground'
                        } ${i === 0 ? 'rounded-l-md' : ''} ${i === modes.length - 1 ? 'rounded-r-md' : ''}`}
                        onClick={() => {
                          setViewMode(mode);
                          onViewChange?.(mode);
                        }}
                      >
                        {mode === 'default'
                          ? gt('Default', {
                              context: 'view mode toggle button label',
                            })
                          : mode === 'v2'
                            ? 'V2'
                            : 'AS400'}
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable wrapper for everything */}
        <div className="min-h-0 flex-1 overflow-y-auto pr-5 relative">
          {isLoadingDetails && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-md">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <SpinnerIcon className="h-5 w-5 animate-spin" />
                <T context="loading state message for transaction details">
                  Loading details...
                </T>
              </div>
            </div>
          )}
          {viewMode === 'v2' && canViewV2 ? (
            <div className="py-2 h-full">
              <TransactionViewV2
                transaction={effectiveTransaction}
                fullTransactionData={
                  fullTransactionData as Record<string, unknown> | undefined
                }
                isLoadingDetails={isLoadingDetails}
                navigationInfo={navigationInfo}
                locationLookup={locationLookup}
                onAddToWorkbench={onAddToWorkbench}
                onStageInternal={onStageInternal}
                isActionPending={isActionPending}
                dunsMismatch={dunsMismatch}
                onAddComment={(text) => {
                  addCommentMutation.mutate(
                    { transactionId: effectiveTransaction.id, text },
                    {
                      onSuccess: () => {
                        queryClient.invalidateQueries({
                          queryKey: [
                            `rexel/ship-and-debit/844/transactions/${effectiveTransaction.id}`,
                          ],
                        });
                      },
                    },
                  );
                }}
                onEditComment={async (activityId, text) => {
                  await editCommentMutation.mutateAsync({ activityId, text });
                  queryClient.invalidateQueries({
                    queryKey: [
                      `rexel/ship-and-debit/844/transactions/${effectiveTransaction.id}`,
                    ],
                  });
                }}
                onDeleteComment={(activityId) => {
                  deleteCommentMutation.mutate(
                    { activityId },
                    {
                      onSuccess: () => {
                        queryClient.invalidateQueries({
                          queryKey: [
                            `rexel/ship-and-debit/844/transactions/${effectiveTransaction.id}`,
                          ],
                        });
                      },
                    },
                  );
                }}
                isDeletePending={deleteCommentMutation.isPending}
                currentUser={currentUser}
              />
            </div>
          ) : viewMode === 'as400' ? (
            <div className="py-2 max-w-[900px] mx-auto">
              {isLoadingDetails ? (
                <div className="space-y-2 p-4">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ) : fullTransaction ? (
                (() => {
                  // Convert snake_case E844D data to rd*-keyed for AS400 display
                  const e844d = toRdKeyed(fullTransaction);
                  const enrichment = (fullTransaction.enrichment_data ??
                    {}) as Record<string, unknown>;
                  const liveItem = fullTransactionData?.itemMasterData?.[0] as
                    | {
                        englishDescription?: string;
                        frenchDescription?: string;
                      }
                    | undefined;
                  const itemDesc: ItemDescriptions = {
                    english:
                      liveItem?.englishDescription ||
                      String(enrichment.itemDescEnglish || ''),
                    french:
                      liveItem?.frenchDescription ||
                      String(enrichment.itemDescFrench || ''),
                  };
                  const handleAs400Edit = () => {
                    // Posted transactions are frozen — never enter edit mode.
                    if (isPostedTx) return;
                    setAs400Error(null);
                    const initial: Record<string, string> = {};
                    for (const rdKey of Object.keys(AS400_EDITABLE_RD_KEYS)) {
                      initial[rdKey] = d(e844d, rdKey);
                    }
                    setAs400EditValues(initial);
                    setAs400Editing(true);
                  };

                  const handleAs400Cancel = () => {
                    setAs400Editing(false);
                    setAs400EditValues({});
                  };

                  const handleAs400Save = () => {
                    if (!transaction) return;
                    setAs400Error(null);
                    const updates: Record<string, unknown> = {};
                    const invalidRdKeys: string[] = [];
                    for (const [rdKey, value] of Object.entries(
                      as400EditValues,
                    )) {
                      const snakeKey = RD_TO_SNAKE[rdKey];
                      if (!snakeKey) continue;
                      const original = d(e844d, rdKey);
                      if (value !== original) {
                        const mode = AS400_EDITABLE_RD_KEYS[rdKey];
                        if (mode !== 'decimal') {
                          updates[snakeKey] = value || null;
                          continue;
                        }
                        // Same rule as the pricing form: a partially-numeric
                        // price must block the save, never persist its prefix.
                        const parsed = parseDecimalInput(value);
                        if (!parsed.ok) {
                          invalidRdKeys.push(rdKey);
                          continue;
                        }
                        updates[snakeKey] = parsed.value;
                      }
                    }
                    if (invalidRdKeys.length > 0) {
                      setAs400Error(
                        `${gt('Enter a valid number for:', {
                          context:
                            'error shown when a price field is not a valid number',
                        })} ${invalidRdKeys.join(', ')}`,
                      );
                      return;
                    }
                    if (Object.keys(updates).length === 0) {
                      setAs400Editing(false);
                      return;
                    }
                    updateStagedMutation.mutate(
                      {
                        transactionId: effectiveTransaction.id,
                        updates,
                      },
                      {
                        onSuccess: () => {
                          setAs400Editing(false);
                          setAs400EditValues({});
                          setAs400Error(null);
                          showToast({
                            message: gt('Transaction updated successfully', {
                              context: 'success toast message',
                            }),
                            title: gt('Success', {
                              context: 'success toast title',
                            }),
                            color: 'success',
                            icon: 'check',
                          });
                          queryClient.invalidateQueries({
                            queryKey: [
                              `rexel/ship-and-debit/844/transactions/${effectiveTransaction.id}`,
                            ],
                          });
                        },
                        onError: (error: Error) => {
                          setAs400Error(
                            error.message ||
                              gt('Failed to save changes', {
                                context:
                                  'error message when saving transaction fails',
                              }),
                          );
                        },
                      },
                    );
                  };

                  return (
                    <>
                      <div className="space-y-1 pb-1">
                        <div className="flex justify-end">
                          {as400Editing ? (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => {
                                  handleAs400Cancel();
                                  setAs400Error(null);
                                }}
                              >
                                <T context="button label to cancel editing">
                                  Cancel
                                </T>
                              </Button>
                              <Button
                                size="sm"
                                className="h-7 text-xs"
                                onClick={handleAs400Save}
                                disabled={updateStagedMutation.isPending}
                              >
                                {updateStagedMutation.isPending ? (
                                  <span className="animate-spin mr-1">
                                    &#8987;
                                  </span>
                                ) : (
                                  <FloppyDiskIcon className="h-3.5 w-3.5 mr-1" />
                                )}
                                {updateStagedMutation.isPending
                                  ? gt('Saving...', {
                                      context:
                                        'button label while save is in progress',
                                    })
                                  : gt('Save', {
                                      context: 'button label to save changes',
                                    })}
                              </Button>
                            </div>
                          ) : canEdit && !isPostedTx ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={handleAs400Edit}
                            >
                              <PencilSimpleIcon className="h-3.5 w-3.5 mr-1" />
                              <T context="button label to start editing AS400 transaction fields">
                                Edit
                              </T>
                            </Button>
                          ) : null}
                        </div>
                        {as400Error && (
                          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                            {as400Error}
                          </div>
                        )}
                      </div>
                      <AS400DisplayView
                        data={e844d}
                        itemDescriptions={itemDesc}
                        orderdadData={fullTransactionData?.orderdadData}
                        isEditing={as400Editing}
                        editValues={as400EditValues}
                        onFieldChange={(rdKey, value) =>
                          setAs400EditValues((prev) => ({
                            ...prev,
                            [rdKey]: value,
                          }))
                        }
                      />
                      <div className="flex justify-end pt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadE844DExcel(e844d, itemDesc)}
                        >
                          <DownloadSimpleIcon className="h-4 w-4 mr-1" />
                          <T context="button label to download AS400 transaction data as Excel file">
                            Download Excel
                          </T>
                        </Button>
                      </div>
                    </>
                  );
                })()
              ) : (
                <p className="text-sm text-muted-foreground p-4">
                  <T context="empty state message when AS400 E844D data is not available for a transaction">
                    E844D data not available for this transaction.
                  </T>
                </p>
              )}
            </div>
          ) : (
            <>
              {/* Top two-column: left = top details, right = comments */}
              <div className="flex gap-4">
                {/* Left column */}
                <div className="flex-1 min-w-0 space-y-3 pr-2">
                  {/* Identity bar */}
                  <div className="flex items-start gap-6 flex-wrap">
                    <div className="space-y-0.5">
                      <MetadataItem
                        label={gt('Company', {
                          context: 'transaction identity field label',
                        })}
                        value={
                          locationLookup.companyName
                            ? `${locationLookup.companyName} (${companyCode})`
                            : companyCode || '\u2014'
                        }
                      />
                      <MetadataItem
                        label={gt('Division', {
                          context: 'transaction identity field label',
                        })}
                        value={
                          locationLookup.divisionName
                            ? `${locationLookup.divisionName} (${divisionCode})`
                            : divisionCode || '\u2014'
                        }
                      />
                      <MetadataItem
                        label={gt('Branch', {
                          context: 'transaction identity field label',
                        })}
                        value={
                          locationLookup.branchName
                            ? `${locationLookup.branchName} (${branchCode})`
                            : branchCode || '\u2014'
                        }
                      />
                    </div>
                    <MetadataItem
                      label={gt('Supplier', {
                        context: 'transaction identity field label',
                      })}
                      value={String(
                        txField(effectiveTransaction, 'supplier_name') ??
                          txField(effectiveTransaction, 'trading_partner') ??
                          '',
                      )}
                    />
                    <MetadataItem
                      label={gt('Invoice #', {
                        context:
                          'transaction identity field label for invoice number',
                      })}
                      value={String(
                        txField(effectiveTransaction, 'invoice_no') ?? '',
                      )}
                    />
                    <MetadataItem
                      label={gt('Order #', {
                        context:
                          'transaction identity field label for order number',
                      })}
                      value={
                        String(
                          txField(effectiveTransaction, 'order_no') ?? '',
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('Order Line #', {
                        context:
                          'transaction identity field label for order line number',
                      })}
                      value={
                        String(
                          txField(effectiveTransaction, 'order_line_no') ?? '',
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('Batch Ctrl#', {
                        context:
                          'transaction identity field label for batch control number',
                      })}
                      value={
                        String(
                          txField(effectiveTransaction, 'extract_control_no') ??
                            '',
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('Contract #', {
                        context:
                          'transaction identity field label for contract number',
                      })}
                      value={String(
                        txField(effectiveTransaction, 'supplier_contract_no') ??
                          '',
                      )}
                    />
                    <MetadataItem
                      label={gt('Ship Type', {
                        context: 'transaction identity field label',
                      })}
                      value={
                        txField(effectiveTransaction, 'ship_type') ? (
                          <Badge variant="outline" className="text-xs">
                            {String(txField(effectiveTransaction, 'ship_type'))}
                          </Badge>
                        ) : (
                          ''
                        )
                      }
                    />
                    <MetadataItem
                      label={gt('Rebate Type', {
                        context: 'transaction identity field label',
                      })}
                      value={
                        String(
                          txField(effectiveTransaction, 'rebate_type') ?? '',
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('EDI Supplier', {
                        context:
                          'transaction identity field label for EDI supplier flag',
                      })}
                      value={
                        txField(effectiveTransaction, 'edi_supplier_flag') ===
                        'Y' ? (
                          <Badge
                            variant="secondary"
                            className="bg-green-100 text-green-700 text-xs"
                          >
                            <T context="EDI supplier flag value: yes">Yes</T>
                          </Badge>
                        ) : (
                          gt('No', { context: 'EDI supplier flag value: no' })
                        )
                      }
                    />
                  </div>

                  <div className="border-t" />

                  {/* Dates: Invoice Date (prominent), Ship Date, Order Date */}
                  <div className="flex items-end gap-8">
                    <div className="min-w-0">
                      <p className="text-[11px] text-muted-foreground leading-tight">
                        <T context="transaction date field label">
                          Invoice Date
                        </T>
                      </p>
                      <div className="text-sm font-semibold">
                        {formatDateField(
                          txField(effectiveTransaction, 'invoice_date'),
                        ) || '\u2014'}
                      </div>
                    </div>
                    <MetadataItem
                      label={gt('Ship Date', {
                        context: 'transaction date field label',
                      })}
                      value={
                        formatDateField(
                          txField(effectiveTransaction, 'ship_date'),
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('Order Date', {
                        context: 'transaction date field label',
                      })}
                      value={
                        formatDateField(
                          txField(effectiveTransaction, 'order_date'),
                        ) || '\u2014'
                      }
                    />
                  </div>

                  <div className="border-t" />

                  {/* Customer | Item | Agreement | Pricing */}
                  <div className="grid grid-cols-[3fr_3fr_2fr_3fr] gap-6">
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium text-muted-foreground mb-1">
                        <T context="transaction detail section heading">
                          Customer
                        </T>
                      </p>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p
                              className="text-sm font-medium truncate"
                              tabIndex={0}
                            >
                              {customerDisplay || '\u2014'}
                            </p>
                          </TooltipTrigger>
                          {customerDisplay && (
                            <TooltipContent className="max-w-[300px]">
                              <p>{customerDisplay}</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                      {customerLocation && (
                        <div className="flex items-baseline gap-1.5 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="customer location sub-label">
                              Location:
                            </T>
                          </span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="text-muted-foreground truncate"
                                  tabIndex={0}
                                >
                                  {customerLocation}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[300px]">
                                <p>{customerLocation}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      )}
                      <div className="mt-1 space-y-0.5">
                        <div className="flex items-baseline gap-1.5 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="customer name and city sub-label">
                              Name/City:
                            </T>
                          </span>
                          <span className="font-medium truncate">
                            {String(
                              txField(
                                effectiveTransaction,
                                'customer_name_city',
                              ) ?? '',
                            ) || '\u2014'}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1.5 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="customer ship-to number sub-label">
                              Ship-To #:
                            </T>
                          </span>
                          <span className="font-medium">
                            {String(
                              txField(effectiveTransaction, 'ship_to_no') ?? '',
                            ) || '\u2014'}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1.5 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="customer sold-to number sub-label">
                              Sold-To #:
                            </T>
                          </span>
                          <span className="font-medium">
                            {String(
                              txField(effectiveTransaction, 'sold_to_no') ?? '',
                            ) || '\u2014'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium text-muted-foreground mb-1">
                        <T context="transaction detail section heading">Item</T>
                      </p>
                      <p className="text-xs">
                        <span className="text-muted-foreground">
                          {gt('Quantity', {
                            context: 'item quantity sub-label',
                          })}
                          {txField(effectiveTransaction, 'quantity_uom')
                            ? ` (${String(txField(effectiveTransaction, 'quantity_uom'))})`
                            : ''}
                          :
                        </span>{' '}
                        <span
                          className={`font-medium ${
                            txField(effectiveTransaction, 'quantity') != null &&
                            Number(txField(effectiveTransaction, 'quantity')) <
                              0
                              ? 'text-red-600'
                              : ''
                          }`}
                        >
                          {txField(effectiveTransaction, 'quantity') != null
                            ? formatNumber(
                                Number(
                                  txField(effectiveTransaction, 'quantity'),
                                ),
                              )
                            : '\u2014'}
                        </span>
                      </p>
                      <div className="flex items-baseline gap-1.5 text-xs min-w-0">
                        <span className="text-muted-foreground shrink-0">
                          <T context="item product code sub-label">
                            Product Code:
                          </T>
                        </span>
                        <span
                          className="text-sm font-medium truncate min-w-0"
                          title={
                            String(
                              txField(
                                effectiveTransaction,
                                'item_with_prefix',
                              ) ?? '',
                            ) || undefined
                          }
                        >
                          {String(
                            txField(effectiveTransaction, 'item_with_prefix') ??
                              '',
                          ) || '\u2014'}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-1.5 text-xs">
                        <span className="text-muted-foreground shrink-0">
                          <T context="item description sub-label">
                            Description:
                          </T>
                        </span>
                        <span
                          className="text-xs text-muted-foreground line-clamp-2"
                          title={
                            fullTransactionData?.itemMasterData?.[0]
                              ?.englishDescription ?? undefined
                          }
                        >
                          {fullTransactionData?.itemMasterData?.[0]
                            ?.englishDescription || '\u2014'}
                        </span>
                      </div>
                      <div className="mt-1 space-y-0.5">
                        <div className="flex items-baseline gap-1.5 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="item supplier code sub-label">
                              Supplier Code:
                            </T>
                          </span>
                          <span className="font-medium">
                            {String(
                              txField(
                                effectiveTransaction,
                                'vendor_product_no',
                              ) ?? '',
                            ) || '\u2014'}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1.5 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="item UPC code from 844D sub-label">
                              UPC Code:
                            </T>
                          </span>
                          <span className="font-medium">
                            {String(
                              txField(effectiveTransaction, 'upc_code') ?? '',
                            ) || '\u2014'}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1.5 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="item stocking unit of measure sub-label">
                              Stocking UOM:
                            </T>
                          </span>
                          <span className="font-medium">
                            {String(
                              txField(effectiveTransaction, 'uom') ?? '',
                            ) || '\u2014'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground mb-1 flex items-center gap-1">
                        <T context="transaction detail section heading">
                          Agreement
                        </T>
                        {isAgreementExpired && (
                          <Badge className="text-[10px] px-1 py-0 border-red-400 bg-red-100 text-red-700">
                            <T context="agreement status badge indicating the agreement has expired">
                              Expired
                            </T>
                          </Badge>
                        )}
                      </p>
                      {isLoadingDetails ? (
                        <Skeleton className="h-4 w-3/4" />
                      ) : (
                        <div className="space-y-0.5">
                          <div className="flex items-baseline gap-1.5 text-xs">
                            <span className="text-muted-foreground shrink-0">
                              <T context="agreement number sub-label">
                                Number:
                              </T>
                            </span>
                            <span className="font-medium">
                              {agreementNumber || '\u2014'}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-1.5 text-xs">
                            <span className="text-muted-foreground shrink-0">
                              <T context="agreement SPA cost sub-label">
                                SPA Cost:
                              </T>
                            </span>
                            <span className="font-medium">
                              {formatDollar4(
                                ftField('unit_price_quoted') as number | null,
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground mb-1">
                        <T context="transaction detail section heading">
                          Pricing
                        </T>
                      </p>
                      <div className="space-y-0.5 min-w-0">
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="pricing claim amount sub-label">
                              Claim Amt:
                            </T>
                          </span>
                          <span
                            className={`font-medium tabular-nums text-right ${(() => {
                              const v = isEditing
                                ? parseNullableDecimal(formData.claimAmount)
                                : (txField(
                                    effectiveTransaction,
                                    'claim_amount',
                                  ) as number | null);
                              return v != null && v < 0 ? 'text-red-600' : '';
                            })()}`}
                          >
                            {formatDollar(
                              isEditing
                                ? parseNullableDecimal(formData.claimAmount)
                                : (txField(
                                    effectiveTransaction,
                                    'claim_amount',
                                  ) as number | null),
                            )}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="pricing wholesale price sub-label">
                              Wholesale Price:
                            </T>
                          </span>
                          <span className="font-medium tabular-nums text-right">
                            {formatDollar4(
                              isEditing
                                ? parseNullableDecimal(formData.buyerCost)
                                : (txField(
                                    effectiveTransaction,
                                    'unit_price_wholesale',
                                  ) as number | null),
                            )}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="pricing quoted price sub-label">
                              Quoted Price:
                            </T>
                          </span>
                          <span className="font-medium tabular-nums text-right">
                            {formatDollar4(
                              isEditing
                                ? parseNullableDecimal(
                                    formData.coreCostOverride,
                                  )
                                : (txField(
                                    effectiveTransaction,
                                    'unit_price_quoted',
                                  ) as number | null),
                            )}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="pricing last PO cost sub-label">
                              Last PO Cost:
                            </T>
                          </span>
                          <span className="font-medium tabular-nums text-right">
                            {formatDollar4(
                              txField(effectiveTransaction, 'landed_cost') as
                                number | null,
                            )}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="pricing last PO number sub-label">
                              Last PO #:
                            </T>
                          </span>
                          <span className="font-medium tabular-nums text-right">
                            {(() => {
                              const v = txField(
                                effectiveTransaction,
                                'last_po_number',
                              );
                              const s = v == null ? '' : String(v);
                              return s || '-';
                            })()}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="pricing order time override core cost sub-label">
                              Order Time Override Core:
                            </T>
                          </span>
                          <span className="font-medium tabular-nums text-right">
                            {formatDollar4(
                              ((
                                fullTransactionData?.e844sdRaw as Record<
                                  string,
                                  unknown
                                > | null
                              )?.['order_time_override_core_agreement_cost'] as
                                number | null) ?? null,
                            )}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="pricing invoice time override core cost sub-label">
                              Invoice Time Override Core:
                            </T>
                          </span>
                          <span className="font-medium tabular-nums text-right">
                            {formatDollar4(
                              ((
                                fullTransactionData?.e844sdRaw as Record<
                                  string,
                                  unknown
                                > | null
                              )?.[
                                'invoice_time_override_core_agreement_cost'
                              ] as number | null) ?? null,
                            )}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="pricing invoice time branch buyer cost sub-label">
                              Inv Time Branch Buyer Cost:
                            </T>
                          </span>
                          <span className="font-medium tabular-nums text-right">
                            {formatDollar4(
                              ((
                                fullTransactionData?.e844sdRaw as Record<
                                  string,
                                  unknown
                                > | null
                              )?.['inv_time_branch_buyer_cost'] as
                                number | null) ?? null,
                            )}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="pricing invoice time buyer cost sub-label">
                              Invoice Time Buyer Cost:
                            </T>
                          </span>
                          <span className="font-medium tabular-nums text-right">
                            {formatDollar4(
                              ((
                                fullTransactionData?.e844sdRaw as Record<
                                  string,
                                  unknown
                                > | null
                              )?.['invoice_time_buyer_cost'] as
                                number | null) ?? null,
                            )}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="pricing order time branch buyer cost sub-label">
                              Order Time Branch Buyer Cost:
                            </T>
                          </span>
                          <span className="font-medium tabular-nums text-right">
                            {formatDollar4(
                              ((
                                fullTransactionData?.e844sdRaw as Record<
                                  string,
                                  unknown
                                > | null
                              )?.['order_time_branch_buyer_cost'] as
                                number | null) ?? null,
                            )}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                          <span className="text-muted-foreground shrink-0">
                            <T context="pricing order time buyer cost sub-label">
                              Order Time Buyer Cost:
                            </T>
                          </span>
                          <span className="font-medium tabular-nums text-right">
                            {formatDollar4(
                              ((
                                fullTransactionData?.e844sdRaw as Record<
                                  string,
                                  unknown
                                > | null
                              )?.['order_time_buyer_cost'] as number | null) ??
                                null,
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t" />

                  {/* Locations & Codes */}
                  <div className="grid grid-cols-6 gap-x-4 gap-y-1.5">
                    <MetadataItem
                      label={gt('Sell Loc', {
                        context:
                          'transaction location field label for selling location code',
                      })}
                      value={
                        String(
                          txField(effectiveTransaction, 'selling_location') ??
                            '',
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('Ship Loc', {
                        context:
                          'transaction location field label for shipping location code',
                      })}
                      value={
                        String(
                          txField(effectiveTransaction, 'shipping_location') ??
                            '',
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('Sell Loc Name', {
                        context:
                          'transaction location field label for selling location name',
                      })}
                      value={
                        String(
                          txField(
                            effectiveTransaction,
                            'short_sell_location_name',
                          ) ?? '',
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('Ship Loc Name', {
                        context:
                          'transaction location field label for shipping location name',
                      })}
                      value={
                        String(
                          txField(
                            effectiveTransaction,
                            'short_ship_location_name',
                          ) ?? '',
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('Supp. Sell Loc', {
                        context:
                          'transaction location field label for supplier sell location',
                      })}
                      value={
                        String(
                          txField(
                            effectiveTransaction,
                            'supplier_sell_location',
                          ) ?? '',
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('DUNS Loc', {
                        context:
                          'transaction location field label for DUNS GL branch location',
                      })}
                      value={
                        String(
                          txField(effectiveTransaction, 'duns_gl_branch') ?? '',
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('Claim Number', {
                        context: 'transaction field label for claim number',
                      })}
                      value={
                        String(
                          txField(effectiveTransaction, 'claim_number') ?? '',
                        ) || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('Ship-To BPID', {
                        context:
                          'transaction field label for ship-to business partner ID',
                      })}
                      value={
                        String(ftField('suppCustShipToBpid') ?? '') || '\u2014'
                      }
                    />
                    <MetadataItem
                      label={gt('Bill-To BPID', {
                        context:
                          'transaction field label for bill-to business partner ID',
                      })}
                      value={
                        String(ftField('suppCustBillToBpid') ?? '') || '\u2014'
                      }
                    />
                  </div>
                  {dunsMismatch && <SpaDunsWarning mismatch={dunsMismatch} />}

                  <div className="border-t" />

                  {/* ORDERDAD \u2014 fetched live from rexel_orderdad */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1.5">
                      <T context="transaction detail section heading for purchase order data">
                        Order Detail
                      </T>
                    </p>
                    <div className="grid grid-cols-6 gap-x-4 gap-y-1.5">
                      <MetadataItem
                        label={gt('Vendor #', {
                          context: 'order detail field label for vendor number',
                        })}
                        value={
                          fullTransactionData?.orderdadData?.supplier ||
                          '\u2014'
                        }
                      />
                      <MetadataItem
                        label={gt('P/O Number', {
                          context:
                            'order detail field label for purchase order number',
                        })}
                        value={
                          String(
                            fullTransactionData?.orderdadData
                              ?.purchaseOrderNumber ?? '',
                          ) || '\u2014'
                        }
                      />
                      <MetadataItem
                        label={gt('P/O Line #', {
                          context:
                            'order detail field label for purchase order line number',
                        })}
                        value={
                          String(
                            fullTransactionData?.orderdadData?.poLineNumber ??
                              '',
                          ) || '\u2014'
                        }
                      />
                      <MetadataItem
                        label={gt('Original Order #', {
                          context:
                            'order detail field label for original order number',
                        })}
                        value={
                          String(
                            fullTransactionData?.orderdadData
                              ?.originalOrderNumber ?? '',
                          ) || '\u2014'
                        }
                      />
                      <MetadataItem
                        label={gt('Sell Agr. Type', {
                          context:
                            'order detail field label for sell agreement type',
                        })}
                        value={
                          fullTransactionData?.orderdadData?.customUse?.[0] ||
                          '\u2014'
                        }
                      />
                      <MetadataItem
                        label={gt('Cost Agr. Type', {
                          context:
                            'order detail field label for cost agreement type',
                        })}
                        value={
                          fullTransactionData?.orderdadData?.customUse?.[1] ||
                          '\u2014'
                        }
                      />
                      <MetadataItem
                        label={gt('Cost Agr. #', {
                          context:
                            'order detail field label for cost agreement number',
                        })}
                        value={
                          fullTransactionData?.orderdadData?.customUse
                            ?.slice(2, 7)
                            ?.trim() || '\u2014'
                        }
                      />
                      <MetadataItem
                        label={gt('Cost Agr. Seq', {
                          context:
                            'order detail field label for cost agreement sequence',
                        })}
                        value={
                          fullTransactionData?.orderdadData?.customUse
                            ?.slice(7, 10)
                            ?.trim() || '\u2014'
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Right column — activity feed */}
                {(() => {
                  // System events only visible to admins
                  const SYSTEM_ACTIVITY_TYPES = new Set([
                    'converted_from_e844sd',
                    'classified_internal',
                    'pricing_recalculated',
                    'bpid_refreshed',
                    'field_modified_by_rule',
                  ]);
                  const isAdminUser = currentUser?.isAdmin ?? false;
                  const visibleActivities = (
                    fullTransactionData?.activities ?? []
                  ).filter(
                    (a) =>
                      isAdminUser || !SYSTEM_ACTIVITY_TYPES.has(a.activityType),
                  );
                  return (
                    <div className="w-[300px] shrink-0 flex flex-col border-l pl-4 max-h-[60vh]">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        <T context="activity feed section heading">Activity</T>
                        {visibleActivities.length > 0 && (
                          <Badge
                            variant="secondary"
                            className="ml-1.5 text-[10px]"
                          >
                            {visibleActivities.length}
                          </Badge>
                        )}
                      </p>
                      <ScrollArea className="flex-1 min-h-0 mb-2">
                        {visibleActivities.length > 0 ? (
                          <div className="space-y-1.5 pr-4">
                            {visibleActivities.map((a) => {
                              const ACTIVITY_LABELS: Record<string, string> = {
                                field_modified: gt('Modified', {
                                  context:
                                    'activity feed badge: a field was modified',
                                }),
                                pricing_recalculated: gt('Recalculated', {
                                  context:
                                    'activity feed badge: pricing was recalculated',
                                }),
                                staged: gt('Staged', {
                                  context:
                                    'activity feed badge: transaction was staged for claiming',
                                }),
                                unstaged: gt('Unstaged', {
                                  context:
                                    'activity feed badge: transaction was unstaged',
                                }),
                                posted: gt('Posted', {
                                  context:
                                    'activity feed badge: transaction was GL-posted',
                                }),
                                unposted: gt('Un-posted', {
                                  context:
                                    'activity feed badge: transaction posting was reversed',
                                }),
                                staged_internal: gt('Staged Internal', {
                                  context:
                                    'activity feed badge: transaction was staged as internal',
                                }),
                                unstaged_internal: gt('Unstaged Internal', {
                                  context:
                                    'activity feed badge: transaction was unstaged from internal',
                                }),
                                sent_via_edi: gt('Sent (EDI)', {
                                  context:
                                    'activity feed badge: transaction was sent via EDI',
                                }),
                                sent_via_email: gt('Sent (Email)', {
                                  context:
                                    'activity feed badge: transaction was sent via email',
                                }),
                                comment_added: gt('Comment', {
                                  context:
                                    'activity feed badge: a comment was added',
                                }),
                                converted_from_e844sd: gt('Imported', {
                                  context:
                                    'activity feed badge: transaction was imported from E844SD',
                                }),
                                classified_internal: gt('Classified', {
                                  context:
                                    'activity feed badge: transaction was classified as internal',
                                }),
                                bpid_refreshed: gt('BPID Refreshed', {
                                  context:
                                    'activity feed badge: business partner ID was refreshed',
                                }),
                                field_modified_by_rule: gt('Rule Applied', {
                                  context:
                                    'activity feed badge: a field was modified by an automated rule',
                                }),
                              };
                              const ACTIVITY_BADGE_COLORS: Record<
                                string,
                                string
                              > = {
                                converted_from_e844sd:
                                  'bg-purple-100 text-purple-700',
                                classified_internal:
                                  'bg-purple-100 text-purple-700',
                                pricing_recalculated:
                                  'bg-purple-100 text-purple-700',
                                bpid_refreshed: 'bg-purple-100 text-purple-700',
                                field_modified_by_rule:
                                  'bg-purple-100 text-purple-700',
                                comment_added: 'bg-green-100 text-green-700',
                                staged: 'bg-amber-100 text-amber-700',
                                staged_internal: 'bg-amber-100 text-amber-700',
                                unstaged: 'bg-amber-100 text-amber-700',
                                unstaged_internal:
                                  'bg-amber-100 text-amber-700',
                                posted: 'bg-blue-100 text-blue-700',
                                unposted: 'bg-amber-100 text-amber-700',
                                sent_via_edi: 'bg-blue-100 text-blue-700',
                                sent_via_email: 'bg-blue-100 text-blue-700',
                                field_modified: 'bg-gray-100 text-gray-700',
                              };
                              const label =
                                ACTIVITY_LABELS[a.activityType] ??
                                a.activityType.replace(/_/g, ' ');
                              const ts = new Date(a.eventAt);
                              const timeStr = isNaN(ts.getTime())
                                ? a.eventAt
                                : format(ts, 'MMM d, h:mm:ss a');
                              const badgeColor =
                                ACTIVITY_BADGE_COLORS[a.activityType] ??
                                'bg-gray-100 text-gray-700';
                              const isComment =
                                a.activityType === 'comment_added';
                              const isOwnComment =
                                isComment && a.userId === currentUser?.id;
                              const isWithinHour =
                                isComment &&
                                Date.now() - new Date(a.createdAt).getTime() <
                                  60 * 60 * 1000;
                              const canModifyComment =
                                isOwnComment && isWithinHour;
                              const isEditingThis = editingCommentId === a.id;
                              const isEdited =
                                isComment &&
                                !!(a.metadata as Record<string, unknown>)
                                  ?.editedAt;
                              return (
                                <div
                                  key={a.id}
                                  className="group flex gap-2 items-start py-1.5 border-b border-border/40 last:border-0"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <span
                                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badgeColor}`}
                                      >
                                        {label}
                                      </span>
                                      <span className="text-[10px] text-muted-foreground">
                                        {a.user?.name ??
                                          gt('System', {
                                            context:
                                              'activity feed author when action was performed by the system',
                                          })}{' '}
                                        &middot; {timeStr}
                                        {isEdited && (
                                          <span className="italic">
                                            {' '}
                                            <T context="activity feed comment edited indicator">
                                              (edited)
                                            </T>
                                          </span>
                                        )}
                                      </span>
                                      {canModifyComment && !isEditingThis && (
                                        <span className="ml-auto flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <button
                                            type="button"
                                            className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                                            onClick={() => {
                                              setEditingCommentId(a.id);
                                              setEditingCommentText(
                                                a.description,
                                              );
                                            }}
                                          >
                                            <PencilSimpleIcon className="h-3 w-3" />
                                          </button>
                                          <button
                                            type="button"
                                            className="p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-50"
                                            disabled={
                                              deleteCommentMutation.isPending
                                            }
                                            onClick={() => {
                                              deleteCommentMutation.mutate(
                                                { activityId: a.id },
                                                {
                                                  onSuccess: () => {
                                                    queryClient.invalidateQueries(
                                                      {
                                                        queryKey: [
                                                          `rexel/ship-and-debit/844/transactions/${effectiveTransaction.id}`,
                                                        ],
                                                      },
                                                    );
                                                  },
                                                },
                                              );
                                            }}
                                          >
                                            <TrashIcon className="h-3 w-3" />
                                          </button>
                                        </span>
                                      )}
                                    </div>
                                    {isEditingThis ? (
                                      <div className="mt-1 space-y-1">
                                        <Textarea
                                          value={editingCommentText}
                                          onChange={(e) =>
                                            setEditingCommentText(
                                              e.target.value,
                                            )
                                          }
                                          rows={2}
                                          className="text-[11px] resize-none"
                                          autoFocus
                                        />
                                        <div className="flex gap-1">
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-5 px-1.5 text-[10px]"
                                            onClick={() => {
                                              editCommentMutation.mutate(
                                                {
                                                  activityId: a.id,
                                                  text: editingCommentText.trim(),
                                                },
                                                {
                                                  onSuccess: () => {
                                                    setEditingCommentId(null);
                                                    queryClient.invalidateQueries(
                                                      {
                                                        queryKey: [
                                                          `rexel/ship-and-debit/844/transactions/${effectiveTransaction.id}`,
                                                        ],
                                                      },
                                                    );
                                                  },
                                                },
                                              );
                                            }}
                                            disabled={
                                              !editingCommentText.trim() ||
                                              editCommentMutation.isPending
                                            }
                                          >
                                            <CheckIcon className="h-3 w-3 mr-0.5" />
                                            <T context="button label to save edited comment">
                                              Save
                                            </T>
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-5 px-1.5 text-[10px]"
                                            onClick={() =>
                                              setEditingCommentId(null)
                                            }
                                          >
                                            <T context="button label to cancel editing a comment">
                                              Cancel
                                            </T>
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-[11px] text-foreground mt-0.5 whitespace-pre-wrap break-words">
                                        {a.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            <T context="empty state message for activity feed">
                              No activity yet
                            </T>
                          </p>
                        )}
                      </ScrollArea>
                      <div className="relative z-10 space-y-1.5 pt-2 border-t bg-background">
                        <Textarea
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          placeholder={gt('Add a comment...', {
                            context: 'placeholder text for comment input field',
                          })}
                          rows={2}
                          className="text-xs resize-none"
                        />
                        <Button
                          size="sm"
                          className="w-full h-7 text-xs"
                          onClick={handleAddComment}
                          disabled={
                            !commentText.trim() || addCommentMutation.isPending
                          }
                        >
                          <T context="button label to submit a new comment">
                            Add Comment
                          </T>
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Full-width bottom sections */}
              <div className="space-y-3">
                <div className="border-t" />

                {/* Editable Fields card + Inventory side by side */}
                <div className="flex gap-4">
                  {/* Editable Fields card + Pricing section stacked */}
                  <div className="w-[280px] shrink-0 space-y-3">
                    <Card>
                      <CardHeader className="py-2 px-3">
                        <CardTitle className="text-xs flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span>
                              <T context="card title for editable transaction pricing fields">
                                Editable Fields
                              </T>
                            </span>
                            <span
                              className={`font-mono text-[11px] ${
                                txField(effectiveTransaction, 'quantity') !=
                                  null &&
                                Number(
                                  txField(effectiveTransaction, 'quantity'),
                                ) < 0
                                  ? 'text-red-600'
                                  : 'text-muted-foreground'
                              }`}
                            >
                              <T context="quantity abbreviation label in editable fields card header">
                                Qty:
                              </T>{' '}
                              {formatTo4Decimals(
                                txField(effectiveTransaction, 'quantity') as
                                  number | null,
                              ) || '—'}
                            </span>
                          </div>
                          {canEdit && (
                            <div className="flex items-center gap-1">
                              {isEditing ? (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={handleCancelEdit}
                                  >
                                    <T context="button label to cancel editing pricing fields">
                                      Cancel
                                    </T>
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={handleSave}
                                    disabled={
                                      !isDirty || updateStagedMutation.isPending
                                    }
                                  >
                                    <FloppyDiskIcon className="h-3 w-3 mr-1" />
                                    {updateStagedMutation.isPending
                                      ? gt('Saving...', {
                                          context:
                                            'button label while save is in progress',
                                        })
                                      : gt('Save', {
                                          context:
                                            'button label to save pricing changes',
                                        })}
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  aria-label={gt('Edit pricing', {
                                    context:
                                      'aria label for edit pricing button',
                                  })}
                                  onClick={() => {
                                    // "Touched" is per edit session. Reset here
                                    // rather than trusting every exit path to
                                    // clean up: a field left marked touched from
                                    // an earlier session is excluded from the
                                    // mid-edit refresh, so its stale value would
                                    // be written back over a newer server value
                                    // when some other field is saved.
                                    touchedFieldsRef.current = new Set();
                                    setSavedFormData({ ...formData });
                                    setIsEditing(true);
                                  }}
                                >
                                  <PencilSimpleIcon className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          )}
                        </CardTitle>
                      </CardHeader>
                      {saveError && (
                        <div className="mx-3 mt-1 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                          {saveError}
                        </div>
                      )}
                      <CardContent className="px-3 pb-3 pt-0">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                          <EditableField
                            label={gt('Contract No', {
                              context:
                                'editable pricing field label for contract number',
                            })}
                            editing={isEditing}
                            value={formData.contractNo}
                            displayValue={formData.contractNo || '\u2014'}
                            onChange={(v) => handleInputChange('contractNo', v)}
                          />
                          <EditableField
                            label={gt('Claim Amount', {
                              context:
                                'editable pricing field label for claim amount (read-only, auto-calculated)',
                            })}
                            editing={isEditing}
                            value={formData.claimAmount}
                            displayValue={
                              formData.claimAmount
                                ? `$${formData.claimAmount}`
                                : '\u2014'
                            }
                            readOnly
                          />
                          <EditableField
                            label={gt('Wholesale Price', {
                              context:
                                'editable pricing field label for wholesale price',
                            })}
                            editing={isEditing}
                            value={formData.buyerCost}
                            displayValue={
                              formData.buyerCost
                                ? `$${formData.buyerCost}`
                                : '\u2014'
                            }
                            onChange={(v) => handleInputChange('buyerCost', v)}
                            inputMode="decimal"
                          />
                          <EditableField
                            label={gt('Quoted Price', {
                              context:
                                'editable pricing field label for quoted price',
                            })}
                            editing={isEditing}
                            value={formData.coreCostOverride}
                            displayValue={
                              formData.coreCostOverride
                                ? `$${formData.coreCostOverride}`
                                : '\u2014'
                            }
                            onChange={(v) =>
                              handleInputChange('coreCostOverride', v)
                            }
                            inputMode="decimal"
                          />
                          <EditableField
                            label={gt('DUNS Location', {
                              context:
                                'editable pricing field label for DUNS GL branch location',
                            })}
                            editing={isEditing}
                            value={formData.dunsLocation}
                            displayValue={formData.dunsLocation || '\u2014'}
                            onChange={(v) =>
                              handleInputChange('dunsLocation', v)
                            }
                          />
                          <EditableField
                            label={gt('Supp. Sell Loc', {
                              context:
                                'editable pricing field label for supplier sell location',
                            })}
                            editing={isEditing}
                            value={formData.suppSellLoc}
                            displayValue={formData.suppSellLoc || '\u2014'}
                            onChange={(v) =>
                              handleInputChange('suppSellLoc', v)
                            }
                          />
                          <EditableField
                            label={gt('Buyer Product No', {
                              context:
                                'editable field label for buyer product number (rdbpc)',
                            })}
                            editing={isEditing}
                            value={formData.buyerProductNo}
                            displayValue={formData.buyerProductNo || '\u2014'}
                            onChange={(v) =>
                              handleInputChange('buyerProductNo', v)
                            }
                          />
                          <EditableField
                            label={gt('UPC Code', {
                              context:
                                'editable pricing field label for UPC code',
                            })}
                            editing={isEditing}
                            value={formData.upcCode}
                            displayValue={formData.upcCode || '\u2014'}
                            onChange={(v) => handleInputChange('upcCode', v)}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Invoice Data */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <PackageIcon className="h-3.5 w-3.5" />{' '}
                      <T context="invoice data section heading">Invoice Data</T>
                    </p>
                    {isLoadingDetails ? (
                      <div className="space-y-1">
                        <Skeleton className="h-6 w-full" />
                        <Skeleton className="h-6 w-full" />
                      </div>
                    ) : fullTransactionData?.invoiceMasterData &&
                      fullTransactionData.invoiceMasterData.length > 0 ? (
                      (() => {
                        const sortedData = [
                          ...fullTransactionData.invoiceMasterData,
                        ];
                        const totalPages = Math.ceil(
                          sortedData.length / INVENTORY_PAGE_SIZE,
                        );
                        const paginatedData = sortedData.slice(
                          inventoryPage * INVENTORY_PAGE_SIZE,
                          (inventoryPage + 1) * INVENTORY_PAGE_SIZE,
                        );
                        return (
                          <div className="border rounded-md">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="text-xs py-1">
                                    <T context="invoice data table column header for product number">
                                      Product #
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-xs py-1">
                                    <T context="invoice data table column header for invoice number">
                                      Invoice #
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-xs py-1">
                                    <T context="invoice data table column header for purchase order number">
                                      PO #
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-xs py-1">
                                    <T context="invoice data table column header for supplier">
                                      Supplier
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-xs py-1">
                                    <T context="invoice data table column header for division">
                                      Division
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-xs py-1">
                                    <T context="invoice data table column header for location">
                                      Location
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-xs py-1 text-right">
                                    <T context="invoice data table column header for total invoice amount">
                                      Total Invoice Amount
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-xs py-1 text-right">
                                    <T context="invoice data table column header for cost">
                                      Cost
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-xs py-1 text-right">
                                    <T context="invoice data table column header for PO cost">
                                      PO Cost
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-xs py-1 text-right">
                                    <T context="invoice data table column header for invoice date">
                                      Invoice Date
                                    </T>
                                  </TableHead>
                                  <TableHead className="text-xs py-1 text-right">
                                    <T context="invoice data table column header for quantity">
                                      Qty
                                    </T>
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {paginatedData.map((item, index) => (
                                  <TableRow key={index}>
                                    <TableCell className="text-xs py-1">
                                      {item.productNumber || ''}
                                    </TableCell>
                                    <TableCell className="text-xs py-1">
                                      {item.invoiceNumber || ''}
                                    </TableCell>
                                    <TableCell className="text-xs py-1">
                                      {item.poNumber || ''}
                                    </TableCell>
                                    <TableCell className="text-xs py-1">
                                      {item.supplierNumber || ''}
                                    </TableCell>
                                    <TableCell className="text-xs py-1">
                                      {item.division || ''}
                                    </TableCell>
                                    <TableCell className="text-xs py-1">
                                      {item.location || ''}
                                    </TableCell>
                                    <TableCell className="text-xs py-1 text-right">
                                      {item.amount != null
                                        ? formatCurrency(item.amount, {
                                            locale: 'en-CA',
                                            currency: 'CAD',
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })
                                        : ''}
                                    </TableCell>
                                    <TableCell className="text-xs py-1 text-right">
                                      {item.cost != null
                                        ? formatCurrency(item.cost, {
                                            locale: 'en-CA',
                                            currency: 'CAD',
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })
                                        : ''}
                                    </TableCell>
                                    <TableCell className="text-xs py-1 text-right">
                                      {item.poCost != null
                                        ? formatCurrency(item.poCost, {
                                            locale: 'en-CA',
                                            currency: 'CAD',
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 4,
                                          })
                                        : ''}
                                    </TableCell>
                                    <TableCell className="text-right text-xs py-1">
                                      {item.invoiceDate
                                        ? (() => {
                                            // INVMAS stores dates as 6-digit YYMMDD integers (e.g. 260317)
                                            const v = item.invoiceDate;
                                            const d = new Date(
                                              2000 + Math.floor(v / 10000),
                                              Math.floor((v % 10000) / 100) - 1,
                                              v % 100,
                                            );
                                            return isNaN(d.getTime())
                                              ? ''
                                              : format(d, 'MMM d, yyyy');
                                          })()
                                        : ''}
                                    </TableCell>
                                    <TableCell className="text-right text-xs py-1">
                                      {formatInvoiceQuantity(item.quantity)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {totalPages > 1 && (
                              <div className="flex items-center justify-between px-3 py-1.5 border-t text-xs">
                                <span className="text-muted-foreground">
                                  {inventoryPage * INVENTORY_PAGE_SIZE + 1}–
                                  {Math.min(
                                    (inventoryPage + 1) * INVENTORY_PAGE_SIZE,
                                    sortedData.length,
                                  )}{' '}
                                  <T context="pagination indicator: X of Y total items">
                                    of <Var>{sortedData.length}</Var>
                                  </T>
                                </span>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    aria-label={gt('Previous page', {
                                      context:
                                        'aria label for previous page button in invoice data table',
                                    })}
                                    onClick={() =>
                                      setInventoryPage((p) =>
                                        Math.max(0, p - 1),
                                      )
                                    }
                                    disabled={inventoryPage === 0}
                                  >
                                    <CaretLeftIcon className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    aria-label={gt('Next page', {
                                      context:
                                        'aria label for next page button in invoice data table',
                                    })}
                                    onClick={() =>
                                      setInventoryPage((p) =>
                                        Math.min(totalPages - 1, p + 1),
                                      )
                                    }
                                    disabled={inventoryPage === totalPages - 1}
                                  >
                                    <CaretRightIcon className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        <T context="empty state message when no invoice data is available for the transaction">
                          No invoice data available
                        </T>
                      </p>
                    )}
                  </div>
                </div>

                {/* Item Master */}
                <div>
                  <p className="text-[11px] font-medium text-muted-foreground mb-2 flex items-center gap-1">
                    <PackageIcon className="h-3.5 w-3.5" />{' '}
                    <T context="item master section heading">Item Master</T>
                  </p>
                  {isLoadingDetails ? (
                    <div className="space-y-1">
                      <Skeleton className="h-6 w-full" />
                      <Skeleton className="h-6 w-full" />
                    </div>
                  ) : fullTransactionData?.itemMasterData &&
                    fullTransactionData?.itemMasterData.length > 0 ? (
                    <div className="space-y-2">
                      {fullTransactionData?.itemMasterData.map(
                        (item, index) => (
                          <Card key={index}>
                            <CardContent className="px-3 py-2">
                              <div className="space-y-1 mb-2">
                                <MetadataItem
                                  label={gt('English Description', {
                                    context:
                                      'item master field label for English product description',
                                  })}
                                  value={item.englishDescription || '\u2014'}
                                />
                                <MetadataItem
                                  label={gt('French Description', {
                                    context:
                                      'item master field label for French product description',
                                  })}
                                  value={item.frenchDescription || '\u2014'}
                                />
                              </div>
                              <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
                                <MetadataItem
                                  label={gt('Product Group', {
                                    context:
                                      'item master field label for product group and sub-group',
                                  })}
                                  value={
                                    [item.productGroup, item.productSubGroup]
                                      .filter(Boolean)
                                      .join(' / ') || '\u2014'
                                  }
                                />
                                <MetadataItem
                                  label={gt('Buyer Cost', {
                                    context:
                                      'item master field label for buyer cost',
                                  })}
                                  value={
                                    item.buyerCost != null
                                      ? formatCurrency(item.buyerCost, {
                                          locale: 'en-CA',
                                          currency: 'CAD',
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 4,
                                        })
                                      : '\u2014'
                                  }
                                />
                                <MetadataItem
                                  label={gt('Last PO Cost', {
                                    context:
                                      'item master field label for last purchase order cost (landed cost)',
                                  })}
                                  value={
                                    item.landedCost != null
                                      ? formatCurrency(item.landedCost, {
                                          locale: 'en-CA',
                                          currency: 'CAD',
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 4,
                                        })
                                      : '\u2014'
                                  }
                                />
                                <MetadataItem
                                  label={gt('Core Cost', {
                                    context:
                                      'item master field label for core cost',
                                  })}
                                  value={
                                    item.coreCost != null
                                      ? formatCurrency(item.coreCost, {
                                          locale: 'en-CA',
                                          currency: 'CAD',
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 4,
                                        })
                                      : '\u2014'
                                  }
                                />
                                <MetadataItem
                                  label={gt('Stocking UOM', {
                                    context:
                                      'item master field label for stocking unit of measure',
                                  })}
                                  value={item.stockingUom || '\u2014'}
                                />
                                <MetadataItem
                                  label={gt('Pricing UOM', {
                                    context:
                                      'item master field label for pricing unit of measure',
                                  })}
                                  value={item.pricingUom || '\u2014'}
                                />
                                <MetadataItem
                                  label={gt('Weight', {
                                    context:
                                      'item master field label for weight',
                                  })}
                                  value={
                                    item.weight != null
                                      ? `${item.weight} ${item.weightUom || ''}`.trim()
                                      : '\u2014'
                                  }
                                />
                                <MetadataItem
                                  label={gt('Company', {
                                    context:
                                      'item master field label for company',
                                  })}
                                  value={item.company || '\u2014'}
                                />
                                <MetadataItem
                                  label={gt('Division', {
                                    context:
                                      'item master field label for division',
                                  })}
                                  value={item.division || '\u2014'}
                                />
                                <MetadataItem
                                  label={gt('Location', {
                                    context:
                                      'item master field label for location',
                                  })}
                                  value={item.location || '\u2014'}
                                />
                              </div>
                            </CardContent>
                          </Card>
                        ),
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      <T context="empty state message when no item master data is available">
                        No item master data available
                      </T>
                    </p>
                  )}
                </div>
              </div>

              {/* Small gap before Raw JSON section */}
              <div className="pt-2.5" />

              {/* Raw E844SD — original source data (123 columns) */}
              {rawE844sdJson && (
                <div className="rounded-md border">
                  <button
                    type="button"
                    aria-expanded={rawE844sdOpen}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => setRawE844sdOpen((v) => !v)}
                  >
                    <CaretDownIcon
                      className={`h-3 w-3 text-muted-foreground transition-transform ${rawE844sdOpen ? '' : '-rotate-90'}`}
                    />
                    <CodeIcon className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      <T context="collapsible section label for raw E844SD source data">
                        Raw E844SD Data (Source)
                      </T>
                    </span>
                    <span className="text-[10px] text-muted-foreground ml-auto">
                      <T context="count of fields in raw E844SD data">
                        <Var>
                          {
                            Object.keys(fullTransactionData?.e844sdRaw ?? {})
                              .length
                          }
                        </Var>{' '}
                        fields
                      </T>
                    </span>
                  </button>
                  {rawE844sdOpen && (
                    <div className="border-t px-3 py-2">
                      <pre className="text-[10px] font-mono bg-muted/50 rounded p-2 whitespace-pre-wrap max-h-[400px] overflow-auto">
                        {rawE844sdJson}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Raw E844D — converted data (91 columns) */}
              {rawE844dJson && (
                <div className="rounded-md border">
                  <button
                    type="button"
                    aria-expanded={rawE844dOpen}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => setRawE844dOpen((v) => !v)}
                  >
                    <CaretDownIcon
                      className={`h-3 w-3 text-muted-foreground transition-transform ${rawE844dOpen ? '' : '-rotate-90'}`}
                    />
                    <CodeIcon className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      <T context="collapsible section label for raw E844D converted data">
                        Raw E844D Data (Converted)
                      </T>
                    </span>
                    <span className="text-[10px] text-muted-foreground ml-auto">
                      <T context="count of fields in raw E844D data">
                        <Var>
                          {Object.keys(fullTransactionData?.e844d ?? {}).length}
                        </Var>{' '}
                        fields
                      </T>
                    </span>
                  </button>
                  {rawE844dOpen && (
                    <div className="border-t px-3 py-2">
                      <pre className="text-[10px] font-mono bg-muted/50 rounded p-2 whitespace-pre-wrap max-h-[400px] overflow-auto">
                        {rawE844dJson}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {/* end scrollable wrapper */}

        {/* Posted-but-not-sent rows are frozen, but UPC — a pure identifier with
            no effect on the claim amount or GL posting — may still be filled in.
            `isPostedTx` already means status==='staged' AND posted_at set (the
            same pair the backend freezes on), so it matches the rows whose
            UPC-only edit the backend accepts. Own small component; keyed so it
            remounts (resets) per transaction. */}
        {isPostedTx && effectiveTransaction && (
          <PostedUpcEditor
            key={effectiveTransaction.id}
            transactionId={effectiveTransaction.id}
            currentUpc={String(txField(effectiveTransaction, 'upc_code') ?? '')}
          />
        )}

        {/* Footer */}
        <div className="flex justify-between pt-3 border-t">
          <Button variant="outline" size="sm" onClick={onClose}>
            <XIcon className="h-4 w-4 mr-1" />
            <T context="button label to close the transaction modal">Close</T>
          </Button>
          <div className="flex gap-2">
            {onDelete && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleDelete}
                disabled={isActionPending || isPostedTx}
              >
                <ProhibitIcon className="h-4 w-4 mr-1" />
                <T context="button label to mark a ship-and-debit transaction as no-claim (excluded from claiming)">
                  No-Claim
                </T>
              </Button>
            )}
            {onUnstage &&
              (isPostedTx ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* span wrapper so the tooltip still fires on a disabled button */}
                      <span tabIndex={0}>
                        <Button size="sm" variant="outline" disabled>
                          <ProhibitIcon className="h-4 w-4 mr-1" />
                          <T context="button label to unstage a transaction, returning it to the pending pool">
                            Unstage
                          </T>
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <T context="tooltip explaining why a posted ship-and-debit transaction cannot be unstaged">
                        This transaction has been posted and can no longer be
                        unstaged.
                      </T>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleUnstage}
                  disabled={isActionPending}
                >
                  <ArrowUUpLeftIcon className="h-4 w-4 mr-1" />
                  <T context="button label to unstage a transaction, returning it to the pending pool">
                    Unstage
                  </T>
                </Button>
              ))}
            {onStageInternal && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleStageInternal}
                disabled={isActionPending || isPostedTx}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                <T context="button label to stage a transaction as an internal ship-and-debit claim">
                  Stage Internal
                </T>
              </Button>
            )}
            {onAddToWorkbench && (
              <Button
                size="sm"
                onClick={handleAddToWorkbench}
                disabled={isActionPending || isPostedTx}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                <T context="verb, move ship-and-debit transaction into the staging queue for processing">
                  Stage
                </T>
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
